// Registry-based chart options factory for modular extension.
// Each builder receives (basicProps, extendedProps, datapoints) and returns ECharts option object.
import { isChartType } from './widgetTypes';

// Default fallbacks
const defaultCategories = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const defaultSeriesData = [120, 200, 150, 80, 70, 110, 130];

const registry = {};

function getColor(basicProps, extendedProps) {
  return extendedProps?.itemStyle?.color || extendedProps?.lineStyle?.color || basicProps?.textColor || '#005C8E';
}

function normalizePrecision(precision) {
  const parsed = Number(precision);
  if (!isFinite(parsed)) return 2;
  return Math.min(5, Math.max(2, Math.round(parsed)));
}

// Format value with precision
function formatValue(value, precision = 2) {
  if (value === null || value === undefined || !isFinite(value)) return '—';
  return Number(value).toFixed(normalizePrecision(precision));
}

function formatKWh(value, precision = 2) {
  const num = Number(value);
  if (!isFinite(num)) return '—';
  const kwh = num / 1000;
  return `${formatValue(kwh, precision)}`;
}

function parseTimestamp(raw) {
  if (raw === undefined || raw === null) return null;
  // Accept number or numeric string. Server seems to send epoch seconds strings (10 digits) or ms (13 digits)
  const s = typeof raw === 'string' ? raw.trim() : raw;
  if (s === '') return null;
  const num = Number(s);
  if (isNaN(num)) return null;
  // If length <= 11 treat as seconds, else assume milliseconds
  return (s.toString().length <= 11) ? num * 1000 : num;
}

function formatTimeLabel(raw) {
  const ts = parseTimestamp(raw);
  if (ts === null) return (raw === undefined || raw === null) ? '' : String(raw);
  const dt = new Date(ts);
  if (isNaN(dt.getTime())) return String(raw);
  return dt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
}

function extractXY(datapoints, { timeAware = false } = {}) {
  if (!Array.isArray(datapoints) || !datapoints.length) {
    if (timeAware) {
      // fabricate simple time series over last hour
      const now = Date.now();
      const step = 10 * 60 * 1000; // 10 min
      const pairs = Array.from({ length: 6 }, (_, i) => [now - (5 - i) * step, defaultSeriesData[i % defaultSeriesData.length]]);
      return { timePairs: pairs };
    }
    return { categories: defaultCategories, values: defaultSeriesData };
  }
  if (timeAware) {
    const pairs = datapoints.map(d => [parseTimestamp(d.x ?? d.label ?? d.timestamp ?? d.time), d.y ?? d.value ?? 0])
      .filter(p => p[0] !== null && p[1] !== null && p[1] !== undefined && isFinite(p[0]) && isFinite(p[1]));
    return { timePairs: pairs };
  }
  const categories = datapoints.map(d => d.x ?? d.label ?? d.datapointName ?? d.alias ?? d.name ?? '');
  const values = datapoints.map(d => {
    const v = d.y ?? d.value ?? 0; return (v !== null && v !== undefined && isFinite(v)) ? v : 0;
  });
  return { categories, values };
}

// Built-in builders
// Line Chart: strictly no area fill (even if extendedProps provides opacity) to keep visual distinction.
registry['Line Chart'] = (basicProps, extendedProps, datapoints) => {
  const hasGroupedSeries =
    Array.isArray(datapoints) &&
    datapoints.some((d) => Array.isArray(d?.points));

  const tagStyles = Array.isArray(extendedProps?.datapoints)
    ? extendedProps.datapoints
    : [];

  const matchTagStyle = (series) =>
    tagStyles.find((tag) => {
      const seriesTagId = series?.tagid || series?.id;
      const tagId = tag?.tagid || tag?.key || tag?.id;
      const seriesName = series?.name;
      const tagNames = [tag?.datapointName, tag?.alias, tag?.tagname, tag?.name, tag?.title, tag?.displayname, tag?.legendname];
      return (
        (seriesTagId && tagId && String(seriesTagId) === String(tagId)) ||
        (seriesName && tagNames.filter(Boolean).includes(seriesName))
      );
    });

  const groupedSeries = hasGroupedSeries
    ? datapoints
        .map((series, idx) => {
          const points = Array.isArray(series?.points)
            ? series.points
                .map((p) => [
                  parseTimestamp(p.x ?? p.timestamp ?? p.time),
                  p.y ?? p.value,
                ])
                .filter(
                  (pair) =>
                    pair[0] !== null &&
                    pair[1] !== null &&
                    pair[1] !== undefined &&
                    isFinite(pair[0]) &&
                    isFinite(pair[1])
                )
            : [];
          const tagStyle = matchTagStyle(series);
          const lineColor =
            series?.color ||
            tagStyle?.lineStyle?.color ||
            tagStyle?.color ||
            extendedProps?.lineStyle?.color ||
            getColor(basicProps, extendedProps);
          const seriesPrecision = normalizePrecision(
            tagStyle?.valuePrecision ?? extendedProps?.valuePrecision ?? 2
          );
          const mergedLabel = {
            ...(extendedProps?.label || {}),
            ...(tagStyle?.label || {}),
          };
          if (!mergedLabel.formatter) {
            mergedLabel.formatter = (params) =>
              formatValue(params?.value?.[1] ?? params?.value, seriesPrecision);
          }

          return {
            name: series?.name || `Series ${idx + 1}`,
            type: 'line',
            data: points,
            smooth: extendedProps?.smooth,
            symbol: tagStyle?.symbol || extendedProps?.symbol,
            lineStyle: {
              ...(extendedProps?.lineStyle || {}),
              ...(tagStyle?.lineStyle || {}),
              color: lineColor,
            },
            areaStyle: undefined,
            label: mergedLabel,
          };
        })
        .filter((s) => Array.isArray(s.data) && s.data.length > 0)
    : [];

  const { timePairs } = extractXY(datapoints, { timeAware: true });
  const safePairs = Array.isArray(timePairs) ? timePairs.filter(p => Array.isArray(p) && p.length === 2 && p[0] !== null && p[1] !== null) : [];
  const precision = extendedProps?.valuePrecision ?? 2;
  const finalSeries = groupedSeries.length
    ? groupedSeries
    : [{
        type: 'line',
        data: Array.isArray(safePairs) ? safePairs : [],
        smooth: extendedProps?.smooth,
        symbol: extendedProps?.symbol,
        lineStyle: extendedProps?.lineStyle,
        areaStyle: undefined,
        label: {
          ...(extendedProps?.label || {}),
          formatter: extendedProps?.label?.formatter || ((params) =>
            formatValue(params?.value?.[1] ?? params?.value, precision)),
        },
      }];

  const hasData = finalSeries.some((s) => Array.isArray(s.data) && s.data.length > 0);
  return {
    color: groupedSeries.length
      ? groupedSeries.map((s) => s?.lineStyle?.color).filter(Boolean)
      : [extendedProps?.lineStyle?.color || getColor(basicProps, extendedProps)],
    backgroundColor: basicProps?.backgroundColor,
    grid: {
      left: '8%',
      right: '10%',
      bottom: '65',
      top: '20',
      containLabel: true
    },
    xAxis: {
      type: 'time',
      name: extendedProps?.xAxisLabel || 'Time',
      nameLocation: 'middle',
      nameGap: 30,
      boundaryGap: ['5%', '2%'],
      nameTextStyle: { fontWeight: 500, color: basicProps?.textColor || '#3C3C3C' },
      axisLabel: { margin: 6, color: '#56636E' },
      splitLine: { show: !!extendedProps?.showGridLines }
    },
    yAxis: {
      type: 'value',
      name: extendedProps?.yAxisLabel || 'Value',
      nameLocation: 'middle',
      axisLabel: { 
        margin: 10, 
        color: '#56636E',
        formatter: (value) => formatValue(value, precision)
      },
      nameRotate: 90,
      nameGap: 65,
      nameTextStyle: { fontWeight: 500, color: basicProps?.textColor || '#3C3C3C' },
      splitLine: { show: !!extendedProps?.showGridLines }
    },
    series: finalSeries,
    tooltip: hasData ? {
      trigger: 'axis',
      formatter: (params) => {
        const rows = Array.isArray(params) ? params : [params];
        if (!rows.length) return '';
        const header = rows[0]?.axisValueLabel || rows[0]?.name || '';
        const lines = rows.map((p) => {
          const seriesName = p?.seriesName || p?.name || '';
          const rawValue = Array.isArray(p?.value)
            ? p.value[p.value.length - 1]
            : p?.value;
          return `${p?.marker || ''}${seriesName}: ${formatValue(rawValue, precision)}`;
        });
        return header ? `${header}<br/>${lines.join('<br/>')}` : lines.join('<br/>');
      }
    } : { show: false },
  };
};
// Distinct Area Chart builder with gradient fill
// Area Chart: uses gradient fill for distinction; opacity configurable.
registry['Area Chart'] = (basicProps, extendedProps, datapoints) => {
  const firstDp = Array.isArray(extendedProps?.datapoints) ? extendedProps.datapoints[0] : null;
  const tagStyles = Array.isArray(extendedProps?.datapoints)
    ? extendedProps.datapoints
    : [];
  const hasGroupedSeries =
    Array.isArray(datapoints) && datapoints.some((d) => Array.isArray(d?.points));
  const precision = normalizePrecision(extendedProps?.valuePrecision ?? 2);
  
  // Helper to convert hex color to rgba with opacity
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };
  
  const resolveTagStyle = (seriesLike) =>
    tagStyles.find((tag) => {
      const seriesTagId = seriesLike?.tagid || seriesLike?.id;
      const tagId = tag?.tagid || tag?.key || tag?.id;
      const seriesName = seriesLike?.name || seriesLike?.tagname;
      const tagNames = [tag?.datapointName, tag?.alias, tag?.tagname, tag?.name, tag?.title, tag?.displayname, tag?.legendname];
      return (
        (seriesTagId && tagId && String(seriesTagId) === String(tagId)) ||
        (seriesName && tagNames.filter(Boolean).includes(seriesName))
      );
    });
  const resolveSeriesPrecision = (seriesLike) => {
    const matchedTag = resolveTagStyle(seriesLike);
    return normalizePrecision(matchedTag?.valuePrecision ?? precision);
  };

  let series = [];
  if (hasGroupedSeries) {
    series = datapoints
      .map((rawSeries, idx) => {
        const points = Array.isArray(rawSeries?.points)
          ? rawSeries.points
              .map((p) => [
                parseTimestamp(p?.x ?? p?.timestamp ?? p?.time),
                p?.y ?? p?.value,
              ])
              .filter(
                (pair) =>
                  pair[0] !== null &&
                  pair[1] !== null &&
                  pair[1] !== undefined &&
                  isFinite(pair[0]) &&
                  isFinite(pair[1])
              )
          : [];
        const tagStyle = resolveTagStyle(rawSeries);
        const lineColor =
          rawSeries?.color ||
          tagStyle?.lineStyle?.color ||
          extendedProps?.lineStyle?.color ||
          getColor(basicProps, extendedProps);
        const areaColor =
          tagStyle?.areaStyle?.color ||
          extendedProps?.areaStyle?.color ||
          lineColor;
        const areaOpacity =
          tagStyle?.areaStyle?.opacity ??
          extendedProps?.areaStyle?.opacity ??
          0.35;
        const seriesPrecision = resolveSeriesPrecision(rawSeries);
        const mergedLabel = {
          ...(extendedProps?.label || {}),
          ...(tagStyle?.label || {}),
        };
        if (!mergedLabel.formatter) {
          mergedLabel.formatter = (params) =>
            formatKWh(params?.value?.[1] ?? params?.value, seriesPrecision);
        }

        return {
          type: 'line',
          name: rawSeries?.name || rawSeries?.tagname || `Series ${idx + 1}`,
          data: points,
          smooth: extendedProps?.smooth,
          symbol: tagStyle?.symbol || extendedProps?.symbol,
          lineStyle: {
            ...(extendedProps?.lineStyle || {}),
            ...(tagStyle?.lineStyle || {}),
            color: lineColor,
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                {
                  offset: 0,
                  color: areaColor.startsWith('#')
                    ? hexToRgba(areaColor, areaOpacity * 1.5)
                    : areaColor,
                },
                {
                  offset: 1,
                  color: areaColor.startsWith('#')
                    ? hexToRgba(areaColor, areaOpacity * 0.1)
                    : `rgba(255,255,255,0.04)`,
                },
              ],
            },
          },
          label: mergedLabel,
        };
      })
      .filter((s) => Array.isArray(s.data) && s.data.length > 0);
  } else {
    const { timePairs } = extractXY(datapoints, { timeAware: true });
    const safePairs = Array.isArray(timePairs)
      ? timePairs.filter(
          (p) =>
            Array.isArray(p) &&
            p.length === 2 &&
            p[0] !== null &&
            p[1] !== null
        )
      : [];
    const lineColor =
      firstDp?.lineStyle?.color ||
      extendedProps?.lineStyle?.color ||
      getColor(basicProps, extendedProps);
    const areaColor =
      firstDp?.areaStyle?.color ||
      extendedProps?.areaStyle?.color ||
      lineColor;
    const areaOpacity =
      firstDp?.areaStyle?.opacity ??
      extendedProps?.areaStyle?.opacity ??
      0.35;
    const fallbackPrecision = normalizePrecision(
      firstDp?.valuePrecision ?? precision
    );

    series = [
      {
        type: 'line',
        data: Array.isArray(safePairs) ? safePairs : [],
        smooth: extendedProps?.smooth,
        symbol: firstDp?.symbol || extendedProps?.symbol,
        lineStyle: {
          ...(extendedProps?.lineStyle || {}),
          ...(firstDp?.lineStyle || {}),
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              {
                offset: 0,
                color: areaColor.startsWith('#')
                  ? hexToRgba(areaColor, areaOpacity * 1.5)
                  : areaColor,
              },
              {
                offset: 1,
                color: areaColor.startsWith('#')
                  ? hexToRgba(areaColor, areaOpacity * 0.1)
                  : `rgba(255,255,255,0.04)`,
              },
            ],
          },
        },
        label: {
          ...(extendedProps?.label || {}),
          ...(firstDp?.label || {}),
          formatter:
            firstDp?.label?.formatter ||
            ((params) =>
              formatKWh(params?.value?.[1] ?? params?.value, fallbackPrecision)),
        },
      },
    ];
  }

  const hasData = series.some((s) => Array.isArray(s.data) && s.data.length > 0);

  return {
    color:
      series.map((s) => s?.lineStyle?.color).filter(Boolean).length > 0
        ? series.map((s) => s?.lineStyle?.color).filter(Boolean)
        : [getColor(basicProps, extendedProps)],
    backgroundColor: basicProps?.backgroundColor,
    grid: {
      left: '8%',
      right: '10%',
      bottom: '65',
      top: '20',
      containLabel: true
    },
    xAxis: {
      type: 'time',
      name: extendedProps?.xAxisLabel || 'Time',
      nameLocation: 'middle',
      nameGap: 30,
      nameTextStyle: { fontWeight: 500, color: basicProps?.textColor || '#3C3C3C' },
      axisLabel: { margin: 6, color: '#56636E' },
      splitLine: { show: !!extendedProps?.showGridLines }
    },
    yAxis: {
      type: 'value',
      name: extendedProps?.yAxisLabel || 'Value',
      nameLocation: 'middle',
      axisLabel: { 
        margin: 10, 
        color: '#56636E',
        formatter: (value) => formatKWh(value, precision)
      },
      nameRotate: 90,
      nameGap: 60,
      nameTextStyle: { fontWeight: 500, color: basicProps?.textColor || '#3C3C3C' },
      splitLine: { show: !!extendedProps?.showGridLines }
    },
    series,
    tooltip: hasData ? {
      trigger: 'axis',
      formatter: (params) => {
        const rows = Array.isArray(params) ? params : [params];
        if (!rows.length) return '';
        const header = rows[0]?.axisValueLabel || rows[0]?.name || '';
        const lines = rows.map((p) => {
          const seriesName = p?.seriesName || p?.name || '';
          const rawValue = Array.isArray(p?.value)
            ? p.value[p.value.length - 1]
            : p?.value;
          const linePrecision = resolveSeriesPrecision({ name: seriesName });
          return `${p?.marker || ''}${seriesName}: ${formatKWh(rawValue, linePrecision)}`;
        });
        return header ? `${header}<br/>${lines.join('<br/>')}` : lines.join('<br/>');
      }
    } : { show: false },
  };
};

registry['Bar Chart'] = (basicProps, extendedProps, datapoints) => {
  const firstDp = Array.isArray(extendedProps?.datapoints) ? extendedProps.datapoints[0] : null;
  const tagStyles = Array.isArray(extendedProps?.datapoints)
    ? extendedProps.datapoints
    : [];
  const precision = normalizePrecision(
    firstDp?.valuePrecision ?? extendedProps?.valuePrecision ?? 2
  );
  const hasGroupedSeries =
    Array.isArray(datapoints) && datapoints.some((d) => Array.isArray(d?.points));
  const resolveTagStyle = (seriesLike) =>
    tagStyles.find((tag) => {
      const seriesTagId = seriesLike?.tagid || seriesLike?.id;
      const tagId = tag?.tagid || tag?.key || tag?.id;
      const seriesName =
        seriesLike?.tagName ||
        seriesLike?.datapointName ||
        seriesLike?.alias ||
        seriesLike?.name ||
        seriesLike?.tagname;
      const tagNames = [tag?.datapointName, tag?.alias, tag?.tagname, tag?.name, tag?.title, tag?.displayname, tag?.legendname];
      return (
        (seriesTagId && tagId && String(seriesTagId) === String(tagId)) ||
        (seriesName && tagNames.filter(Boolean).includes(seriesName))
      );
    });
  const resolveSeriesPrecision = (seriesName) => {
    const match = resolveTagStyle({ tagName: seriesName });
    return normalizePrecision(match?.valuePrecision ?? precision);
  };
  
  // If we have multiple datapoints and stacked mode, create separate series for each
  const hasMultipleDatapoints = Array.isArray(datapoints) && datapoints.length > 1;
  const isStacked = extendedProps?.stacked;
  
  let series = [];
  let categories = [];
  
  if (hasGroupedSeries) {
    const categorySet = new Set();
    const normalizedGrouped = datapoints.map((series, idx) => {
      const tagName =
        series?.datapointName || series?.alias || series?.name || series?.tagname || `Series ${idx + 1}`;
      const points = Array.isArray(series?.points)
        ? series.points
            .map((p) => {
              const rawX = p?.x ?? p?.timestamp ?? p?.time;
              const x = parseTimestamp(rawX);
              const y = p?.y ?? p?.value ?? p?.val;
              return { x, y: Number(y) };
            })
            .filter((p) => p.x !== null && isFinite(p.x) && isFinite(p.y))
        : [];
      points.forEach((p) => categorySet.add(p.x));
      return {
        ...series,
        tagName,
        points,
      };
    });

    categories = Array.from(categorySet).sort((a, b) => a - b);

    series = normalizedGrouped.map((dp) => {
      const valueByX = new Map(dp.points.map((p) => [p.x, p.y]));
      const tagStyle = resolveTagStyle(dp);
      const seriesPrecision = normalizePrecision(
        dp?.valuePrecision ?? tagStyle?.valuePrecision ?? resolveSeriesPrecision(dp.tagName)
      );
      const barColor =
        dp?.itemStyle?.color ||
        tagStyle?.itemStyle?.color ||
        tagStyle?.color ||
        dp?.color ||
        dp?.backgroundColor ||
        extendedProps?.itemStyle?.color;
      return {
        type: 'bar',
        name: dp.tagName,
        data: categories.map((x) =>
          valueByX.has(x) ? valueByX.get(x) : null
        ),
        barWidth: dp?.barWidth || extendedProps?.barWidth,
        stack: isStacked ? 'total' : undefined,
        itemStyle: {
          ...extendedProps?.itemStyle,
          ...(tagStyle?.itemStyle || {}),
          ...(dp?.itemStyle || {}),
          color: barColor,
        },
        showBackground: extendedProps?.showBackground,
        backgroundStyle: extendedProps?.backgroundStyle,
        label: {
          ...(extendedProps?.label || {}),
          ...(dp?.label || {}),
          formatter: dp?.label?.formatter || ((params) =>
            formatKWh(params?.value, seriesPrecision)),
        },
      };
    });
  } else if (hasMultipleDatapoints && isStacked) {
    // Multi-series stacked mode: each datapoint becomes a series
    // Assume each datapoint has x (category) and y (value) or similar structure
    // Extract all unique categories first
    const categorySet = new Set();
    datapoints.forEach(dp => {
      const cat = dp.x || dp.label || dp.category || dp.datapointName || dp.alias || dp.name;
      if (cat) categorySet.add(cat);
    });
    categories = Array.from(categorySet);
    
    // Create a series for each datapoint
    datapoints.forEach((dp, idx) => {
      const seriesName = dp.datapointName || dp.alias || dp.name || dp.tagname || `Series ${idx + 1}`;
      const tagStyle = resolveTagStyle(dp);
      const value = Number(dp.y || dp.value || 0);
      const seriesPrecision = normalizePrecision(
        dp?.valuePrecision ?? tagStyle?.valuePrecision ?? precision
      );
      const barColor =
        dp?.itemStyle?.color ||
        tagStyle?.itemStyle?.color ||
        tagStyle?.color ||
        dp?.color ||
        dp?.backgroundColor ||
        extendedProps?.itemStyle?.color;
      
      series.push({
        type: 'bar',
        name: seriesName,
        data: [value], // Single value per series for now
        barWidth: dp?.barWidth || extendedProps?.barWidth,
        stack: 'total', // All series share same stack group
        itemStyle: {
          ...extendedProps?.itemStyle,
          ...(tagStyle?.itemStyle || {}),
          ...(dp?.itemStyle || {}),
          color: barColor
        },
        showBackground: extendedProps?.showBackground,
        backgroundStyle: extendedProps?.backgroundStyle,
        label: {
          ...(extendedProps?.label || {}),
          ...(dp?.label || {}),
          formatter: dp?.label?.formatter || ((params) =>
            formatKWh(params?.value, seriesPrecision)),
        },
      });
    });
    
    // If no categories were extracted, use datapoint names as categories
    if (categories.length === 0) {
      categories = datapoints.map((dp, idx) => 
        dp.datapointName || dp.alias || dp.name || dp.tagname || `Item ${idx + 1}`
      );
    }
  } else {
    // Single series mode (default behavior)
    const { categories: cats, values } = extractXY(datapoints);
    categories = cats;
    series = [{
      type: 'bar',
      name: firstDp?.datapointName || firstDp?.name || firstDp?.tagname || 'Tag',
      data: Array.isArray(values) ? values : [],
      barWidth: firstDp?.barWidth ?? extendedProps?.barWidth,
      stack: isStacked ? 'total' : undefined,
      itemStyle: {
        ...(extendedProps?.itemStyle || {}),
        ...(firstDp?.itemStyle || {}),
      },
      showBackground: extendedProps?.showBackground,
      backgroundStyle: extendedProps?.backgroundStyle,
      label: {
        ...(extendedProps?.label || {}),
        ...(firstDp?.label || {}),
        formatter: firstDp?.label?.formatter || ((params) =>
          formatKWh(params?.value, precision)),
      },
    }];
  }
  
  return {
    color:
      series.map((s) => s?.itemStyle?.color).filter(Boolean).length > 0
        ? series.map((s) => s?.itemStyle?.color).filter(Boolean)
        : [getColor(basicProps, extendedProps)],
    backgroundColor: basicProps?.backgroundColor,
    grid: {
      left: '10%',
      right: '10%',
      bottom: '95',
      top: '20',
      containLabel: true
    },
    legend: hasMultipleDatapoints && isStacked ? {
      show: true,
      bottom: 0,
      orient: 'horizontal',
      left: 'center',
      textStyle: { color: basicProps?.textColor || '#3C3C3C' }
    } : {
      show: hasGroupedSeries && series.length > 1,
      bottom: 0,
      orient: 'horizontal',
      left: 'center',
      textStyle: { color: basicProps?.textColor || '#3C3C3C' }
    },
    xAxis: {
      type: 'category',
      data: categories,
      name: extendedProps?.xAxisLabel || 'Category',
      nameLocation: 'middle',
      nameGap: 30,
      nameTextStyle: { fontWeight: 500, color: basicProps?.textColor || '#3C3C3C' },
      axisLabel: { 
        margin: 6, 
        color: '#56636E',
        formatter: (value) => formatTimeLabel(value)
      },
      splitLine: { show: !!extendedProps?.showGridLines }
    },
    yAxis: {
      type: 'value',
      name: extendedProps?.yAxisLabel || 'Value',
      nameLocation: 'middle',
      nameGap: 65,
      nameRotate: 90,
      nameTextStyle: { fontWeight: 500, color: basicProps?.textColor || '#3C3C3C' },
      axisLabel: { 
        margin: 10, 
        color: '#56636E',
        formatter: (value) => formatKWh(value, precision)
      },
      splitLine: { show: !!extendedProps?.showGridLines }
    },
    series: series,
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        const rows = Array.isArray(params) ? params : [params];
        if (!rows.length) return '';

        const rawHeader = rows[0]?.axisValueLabel || rows[0]?.name || '';
        const header = formatTimeLabel(rawHeader);
        const lines = rows.map((p) => {
          const tagName = p?.seriesName || p?.name || 'Tag';
          const rawValue = Array.isArray(p?.value)
            ? p.value[p.value.length - 1]
            : p?.value;
          const formattedValue = formatKWh(
            rawValue,
            resolveSeriesPrecision(tagName)
          );
          return `${p?.marker || ''}${tagName}: ${formattedValue}`;
        });

        return header ? `${header}<br/>${lines.join('<br/>')}` : lines.join('<br/>');
      }
    }
  };
};

registry['Pie Chart'] = (basicProps, extendedProps, datapoints) => {
  // Generate default colors for pie segments
  const defaultColors = [
    '#005C8E', '#00A3E0', '#0084CA', '#0066A1', '#004B73',
    '#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6',
    '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'
  ];
  
  const pieData = (Array.isArray(datapoints) && datapoints.length) 
    ? datapoints.map((d, idx) => ({ 
        value: d.value ?? d.y ?? 0, 
        name: d.datapointName ?? d.name ?? d.label ?? d.x ?? `Item ${idx + 1}`,
        valuePrecision: normalizePrecision(
          d?.valuePrecision ?? extendedProps?.datapoints?.[idx]?.valuePrecision ?? extendedProps?.valuePrecision ?? 2
        ),
        itemStyle: {
           color: d.color || extendedProps?.datapoints?.[idx]?.color || defaultColors[idx % defaultColors.length],
           borderColor: d.itemStyle?.borderColor || extendedProps?.datapoints?.[idx]?.itemStyle?.borderColor || extendedProps?.itemStyle?.borderColor,
           borderWidth: d.itemStyle?.borderWidth || extendedProps?.datapoints?.[idx]?.itemStyle?.borderWidth || extendedProps?.itemStyle?.borderWidth,
        }
      })).filter(item => item.value > 0)
    : (extendedProps?.pieData || [
        { value: 1048, name: 'Search Engine' }, 
        { value: 735, name: 'Direct' }, 
        { value: 580, name: 'Email' }, 
        { value: 484, name: 'Union Ads' }, 
        { value: 300, name: 'Video Ads' },
      ]);
  
  return {
    backgroundColor: basicProps?.backgroundColor,
    tooltip: {
      trigger: 'item',
      formatter: (params) => {
        const precision = normalizePrecision(params?.data?.valuePrecision ?? extendedProps?.valuePrecision ?? 2);
        return `${params?.name}: ${formatValue(params?.value, precision)} (${params?.percent}%)`;
      }
    },
    legend: {
      orient: 'horizontal',
      bottom: '0',
      left: 'center',
      textStyle: {
        color: basicProps?.textColor || '#3C3C3C'
      }
    },
    series: [{ 
      type: 'pie', 
      radius: extendedProps?.radius || '60%',
      center: ['50%', '45%'],
      data: pieData,
      startAngle: 0,
      minAngle: 5,
      clockwise: true,
      roseType: extendedProps?.roseType, 
      avoidLabelOverlap: true,
      label: extendedProps?.label || {
        show: true,
        formatter: (params) => {
          const precision = normalizePrecision(params?.data?.valuePrecision ?? extendedProps?.valuePrecision ?? 2);
          return `${params?.name}: ${formatValue(params?.value, precision)} (${params?.percent}%)`;
        },
        position: 'outside'
      },
      labelLine: {
        show: true,
        length: 15,
        length2: 15,
        smooth: false
      },
      emphasis: {
        itemStyle: {
          shadowBlur: 10,
          shadowOffsetX: 0,
          shadowColor: 'rgba(0, 0, 0, 0.5)'
        }
      }
    }]
  };
};

registry['Doughnut Chart'] = (basicProps, extendedProps, datapoints) => {
  const precision = normalizePrecision(extendedProps?.valuePrecision ?? 2);
  const doughnutData = (Array.isArray(datapoints) && datapoints.length)
    ? datapoints.map((d, idx) => ({
        value: Number(d.value ?? d.y ?? d.val ?? 0),
        name: d.label ?? d.x ?? d.datapointName ?? d.name ?? `Slice ${idx + 1}`,
        valuePrecision: normalizePrecision(d.valuePrecision ?? extendedProps?.valuePrecision ?? 2),
        itemStyle: {
          color:
            d?.itemStyle?.color ||
            d?.color ||
            d?.backgroundColor ||
            extendedProps?.itemStyle?.color ||
            undefined,
        },
      }))
    : (extendedProps?.doughnutData || [
        { value: 335, name: 'A' },
        { value: 310, name: 'B' },
        { value: 234, name: 'C' },
        { value: 135, name: 'D' },
      ]);
  const inner = Math.min(90, Math.max(0, Number(extendedProps?.innerRadius ?? 40)));
  const outer = Math.min(95, Math.max(inner + 1, Number(extendedProps?.outerRadius ?? 70)));
  const hasData = doughnutData.some((d) => isFinite(Number(d?.value)));
  return {
    backgroundColor: basicProps?.backgroundColor,
    tooltip: hasData
      ? {
          trigger: 'item',
          formatter: (params) => `${params?.name}: ${formatValue(params?.value, precision)} (${params?.percent}%)`,
        }
      : { show: false },
    legend: {
      orient: 'horizontal',
      bottom: '0',
      left: 'center',
      textStyle: { color: basicProps?.textColor || '#3C3C3C' },
    },
    series: [{
      type: 'pie',
      radius: [`${inner}%`, `${outer}%`],
      center: ['50%', '45%'],
      avoidLabelOverlap: true,
      itemStyle: {
        borderColor: extendedProps?.itemStyle?.borderColor || '#fff',
        borderWidth: extendedProps?.itemStyle?.borderWidth ?? 2,
      },
      label: {
        show: extendedProps?.label?.show ?? true,
        formatter: (params) => {
          const p = normalizePrecision(params?.data?.valuePrecision ?? precision);
          return `${params?.name}: ${formatValue(params?.value, p)} (${params?.percent}%)`;
        },
      },
      data: doughnutData,
    }]
  };
};

registry['Radar Chart'] = (basicProps, extendedProps, datapoints) => {
  const configuredMax = Math.max(1, Number(extendedProps?.radarIndicatorMax ?? 100));
  const values = (Array.isArray(datapoints) && datapoints.length)
    ? datapoints.map((d) => Number(d.value ?? d.y ?? d.val ?? 0))
    : [80, 90, 70, 60];
  const names = (Array.isArray(datapoints) && datapoints.length)
    ? datapoints.map((d, idx) => d.datapointName || d.tagname || d.name || `Metric ${idx + 1}`)
    : ['Metric1', 'Metric2', 'Metric3', 'Metric4'];
  const indicatorColors = (Array.isArray(datapoints) && datapoints.length)
    ? datapoints.map((d) => d?.itemStyle?.color || d?.color || d?.backgroundColor || '#3C3C3C')
    : names.map(() => '#3C3C3C');
  const axisNameRich = names.reduce((acc, _, idx) => {
    acc[`ind${idx}`] = { color: indicatorColors[idx] || '#3C3C3C', fontWeight: 500 };
    return acc;
  }, {});
  const dynamicMax = Math.max(configuredMax, ...values.map((v) => (isFinite(v) ? v : 0)), 1);
  const radarIndicators = names.map((name) => ({ name, max: dynamicMax }));
  const radarData = [{ value: values.map((v) => (isFinite(v) ? v : 0)), name: extendedProps?.title || 'Series' }];
  return {
    backgroundColor: basicProps?.backgroundColor,
    radar: {
      ...extendedProps?.radar,
      indicator: radarIndicators,
      splitNumber: Math.min(10, Math.max(1, Number(extendedProps?.radar?.splitNumber ?? 5))),
      shape: extendedProps?.radar?.shape || 'polygon',
      axisName: {
        formatter: (name) => {
          const idx = names.indexOf(name);
          return idx >= 0 ? `{ind${idx}|${name}}` : name;
        },
        rich: axisNameRich,
      },
    },
    tooltip: {
      trigger: 'item',
      formatter: (params) => {
        const list = Array.isArray(params?.value) ? params.value : [];
        const rows = radarIndicators
          .map((indicator, idx) => `${indicator.name}: ${formatValue(list[idx], extendedProps?.valuePrecision ?? 2)}`)
          .join('<br/>');
        return `${params?.name || 'Series'}<br/>${rows}`;
      },
    },
    series: [{
      type: 'radar',
      data: radarData,
      areaStyle: {
        ...(extendedProps?.areaStyle || {}),
        opacity: Math.min(1, Math.max(0, Number(extendedProps?.areaStyle?.opacity ?? 0.25))),
      },
      lineStyle: {
        ...(extendedProps?.lineStyle || {}),
        width: Math.max(1, Number(extendedProps?.lineStyle?.width ?? 2)),
      },
      symbol: extendedProps?.symbol || 'circle',
      label: extendedProps?.label,
    }]
  };
};

registry['Scatter Chart'] = (basicProps, extendedProps, datapoints) => {
  const precision = normalizePrecision(extendedProps?.valuePrecision ?? 2);
  const scatterDataRaw = (Array.isArray(datapoints) && datapoints.length)
    ? datapoints.map(d => {
        const rawX = d.x ?? d.label ?? d.timestamp ?? d.time;
        const parsedTs = parseTimestamp(rawX);
        const numericX = Number(rawX);
        const xVal = parsedTs !== null ? parsedTs : (isFinite(numericX) ? numericX : null);
        return {
          value: [xVal, Number(d.y ?? d.value ?? d.val ?? 0)],
          itemStyle: {
            color:
              d?.itemStyle?.color ||
              d?.color ||
              d?.backgroundColor ||
              extendedProps?.itemStyle?.color ||
              '#3398DB',
          },
          symbolSize: Math.min(60, Math.max(2, Number(d?.symbolSize ?? extendedProps?.symbolSize ?? 12))),
        };
      })
        .filter(p => p?.value?.[0] !== null && p?.value?.[1] !== null && isFinite(p?.value?.[0]) && isFinite(p?.value?.[1]))
    : (extendedProps?.scatterData || [[Date.now()-300000,8],[Date.now()-240000,15],[Date.now()-180000,25],[Date.now()-120000,35],[Date.now()-60000,45]]);
  const scatterData = scatterDataRaw.filter((p) => {
    const val = Array.isArray(p) ? p : p?.value;
    return Array.isArray(val) && val.length === 2 && !isNaN(val[0]) && !isNaN(val[1]);
  });
  return {
    backgroundColor: basicProps?.backgroundColor,
    xAxis: { type: 'time', name: extendedProps?.xAxisLabel || 'Time', nameLocation:'middle', nameGap:28, axisLabel:{ margin:6, color:'#56636E' }, splitLine: { show: !!extendedProps?.showGridLines } },
    yAxis: { type: 'value', name: extendedProps?.yAxisLabel || 'Value', nameLocation:'middle', nameGap:45, nameRotate:-90, axisLabel:{ margin:10, color:'#56636E' }, splitLine: { show: !!extendedProps?.showGridLines } },
    series: [{
      type: 'scatter',
      data: scatterData,
      symbolSize: (params) => {
        const row = params?.data;
        if (row && !Array.isArray(row) && Number.isFinite(Number(row?.symbolSize))) {
          return Number(row.symbolSize);
        }
        return Math.min(60, Math.max(2, Number(extendedProps?.symbolSize ?? 12)));
      },
      itemStyle: { ...(extendedProps?.itemStyle || {}), color: extendedProps?.itemStyle?.color || '#3398DB' },
      label: extendedProps?.label,
    }],
    tooltip: scatterData.length
      ? {
          trigger: 'item',
          formatter: (params) => {
            const valuePair = Array.isArray(params?.value)
              ? params.value
              : Array.isArray(params?.data?.value)
              ? params.data.value
              : [undefined, undefined];
            const xVal = valuePair[0];
            const yVal = valuePair[1];
            const xText = Number.isFinite(Number(xVal)) ? formatTimeLabel(xVal) : String(xVal ?? '—');
            return `X: ${xText}<br/>Y: ${formatValue(yVal, precision)}`;
          },
        }
      : { show: false },
  };
};

// Gauge Charts
registry['Circular Gauge'] = (basicProps, extendedProps, datapoints) => {
  const value = (Array.isArray(datapoints) && datapoints.length) ? (datapoints[0]?.value ?? datapoints[0]?.y ?? 0) : (extendedProps?.value ?? 75);
  const selectedTag = extendedProps?.datapoints?.[0] || datapoints?.[0] || {};
  const showAlias = Boolean(extendedProps?.showAlias);
  const selectedTagName = showAlias
    ? (
        selectedTag?.alias ||
        selectedTag?.datapointName ||
        selectedTag?.tagname ||
        selectedTag?.name ||
        extendedProps?.title ||
        ''
      )
    : (
        selectedTag?.datapointName ||
        selectedTag?.tagname ||
        selectedTag?.name ||
        extendedProps?.title ||
        selectedTag?.alias ||
        ''
      );
  const selectedTagType =
    selectedTag?.tagType ||
    selectedTag?.tagtype ||
    selectedTag?.dataType ||
    selectedTag?.datatype ||
    selectedTag?.type ||
    '';
  const selectedTagLabel = selectedTagType
    ? `${selectedTagName} (${selectedTagType})`
    : selectedTagName;
  const min = extendedProps?.min ?? 0;
  const max = extendedProps?.max ?? 100;
  const precision = normalizePrecision(extendedProps?.valuePrecision ?? 2);
  const detailFontSize = extendedProps?.detailFontSize ?? 30;
  return {
    backgroundColor: basicProps?.backgroundColor,
    graphic: selectedTagLabel
      ? [
          {
            type: 'text',
            left: 'center',
            bottom: 0,
            style: {
              text: selectedTagLabel,
              fill: extendedProps?.titleColor || '#999',
              font: `${extendedProps?.titleFontSize ?? 14}px Roboto`,
            },
          },
        ]
      : [],
    series: [{
      type: 'gauge',
      startAngle: extendedProps?.startAngle ?? 225,
      endAngle: extendedProps?.endAngle ?? -45,
      min,
      max,
      radius: extendedProps?.radius || '75%',
      center: extendedProps?.center || ['50%', '50%'],
      axisLine: {
        lineStyle: {
          width: extendedProps?.axisLineWidth ?? 20,
          color: extendedProps?.axisLineColor || [
            [0.3, '#67e0e3'],
            [0.7, '#37a2da'],
            [1, '#fd666d']
          ]
        }
      },
      pointer: {
        itemStyle: {
          color: extendedProps?.pointerColor || 'auto'
        },
        length: extendedProps?.pointerLength || '60%',
        width: extendedProps?.pointerWidth ?? 6
      },
      axisTick: {
        distance: extendedProps?.axisTickDistance ?? -20,
        length: extendedProps?.axisTickLength ?? 8,
        lineStyle: {
          color: extendedProps?.axisTickColor || '#fff',
          width: 2
        }
      },
      splitLine: {
        distance: extendedProps?.splitLineDistance ?? -20,
        length: extendedProps?.splitLineLength ?? 20,
        lineStyle: {
          color: extendedProps?.splitLineColor || '#fff',
          width: 4
        }
      },
      axisLabel: {
        distance: extendedProps?.axisLabelDistance ?? -50,
        color: extendedProps?.axisLabelColor || '#999',
        fontSize: extendedProps?.axisLabelFontSize ?? 14,
        formatter: (value) => formatValue(value, precision)
      },
      detail: {
        valueAnimation: true,
        formatter: (value) => formatValue(value, precision),
        color: extendedProps?.detailColor || 'auto',
        fontSize: detailFontSize,
        offsetCenter: extendedProps?.detailOffsetCenter || [0, '82%']
      },
      data: [{ value, name: '' }]
    }]
  };
};

registry['Semi Circular Gauge'] = (basicProps, extendedProps, datapoints) => {
  const value = (Array.isArray(datapoints) && datapoints.length) ? (datapoints[0]?.value ?? datapoints[0]?.y ?? 0) : (extendedProps?.value ?? 65);
  const selectedTag = extendedProps?.datapoints?.[0] || datapoints?.[0] || {};
  const showAlias = Boolean(extendedProps?.showAlias);
  const selectedTagName = showAlias
    ? (
        selectedTag?.alias ||
        selectedTag?.datapointName ||
        selectedTag?.tagname ||
        selectedTag?.name ||
        extendedProps?.title ||
        ''
      )
    : (
        selectedTag?.datapointName ||
        selectedTag?.tagname ||
        selectedTag?.name ||
        extendedProps?.title ||
        selectedTag?.alias ||
        ''
      );
  const selectedTagType =
    selectedTag?.tagType ||
    selectedTag?.tagtype ||
    selectedTag?.dataType ||
    selectedTag?.datatype ||
    selectedTag?.type ||
    '';
  const selectedTagLabel = selectedTagType
    ? `${selectedTagName} (${selectedTagType})`
    : selectedTagName;
  const min = extendedProps?.min ?? 0;
  const max = extendedProps?.max ?? 100;
  const precision = normalizePrecision(extendedProps?.valuePrecision ?? 2);
  return {
    backgroundColor: basicProps?.backgroundColor,
    graphic: selectedTagLabel
      ? [
          {
            type: 'text',
            left: 'center',
            bottom: 0,
            style: {
              text: selectedTagLabel,
              fill: extendedProps?.titleColor || '#999',
              font: `${extendedProps?.titleFontSize ?? 14}px Roboto`,
            },
          },
        ]
      : [],
    grid: {
      left: '5%',
      right: '5%',
      top: '5%',
      bottom: '5%',
      containLabel: false
    },
    series: [{
      type: 'gauge',
      startAngle: 180,
      endAngle: 0,
      min,
      max,
      radius: '90%',
      center: ['50%', '65%'],
      axisLine: {
        lineStyle: {
          width: extendedProps?.axisLineWidth ?? 25,
          color: extendedProps?.axisLineColor || [
            [0.3, '#67e0e3'],
            [0.7, '#37a2da'],
            [1, '#fd666d']
          ]
        }
      },
      pointer: {
        itemStyle: {
          color: extendedProps?.pointerColor || 'auto'
        },
        length: extendedProps?.pointerLength || '70%',
        width: extendedProps?.pointerWidth ?? 6
      },
      axisTick: {
        distance: extendedProps?.axisTickDistance ?? -25,
        length: extendedProps?.axisTickLength ?? 8,
        lineStyle: {
          color: extendedProps?.axisTickColor || '#fff',
          width: 2
        }
      },
      splitLine: {
        distance: extendedProps?.splitLineDistance ?? -25,
        length: extendedProps?.splitLineLength ?? 25,
        lineStyle: {
          color: extendedProps?.splitLineColor || '#fff',
          width: 4
        }
      },
      axisLabel: {
        distance: extendedProps?.axisLabelDistance ?? -55,
        color: extendedProps?.axisLabelColor || '#999',
        fontSize: extendedProps?.axisLabelFontSize ?? 14,
        formatter: (value) => formatValue(value, precision)
      },
      detail: {
        valueAnimation: true,
        formatter: (value) => formatValue(value, precision),
        color: extendedProps?.detailColor || 'auto',
        fontSize: extendedProps?.detailFontSize ?? 30,
        offsetCenter: extendedProps?.detailOffsetCenter || [0, '-20%']
      },
      data: [{ value, name: '' }]
    }]
  };
};

registry['Linear Gauge'] = (basicProps, extendedProps, datapoints) => {
  const firstPoint = Array.isArray(datapoints) && datapoints.length ? datapoints[0] : null;
  const groupedPoint =
    Array.isArray(firstPoint?.points) && firstPoint.points.length
      ? firstPoint.points[firstPoint.points.length - 1]
      : null;
  const rawValue =
    firstPoint?.value ??
    firstPoint?.y ??
    firstPoint?.val ??
    groupedPoint?.value ??
    groupedPoint?.y ??
    groupedPoint?.val ??
    extendedProps?.value ??
    70;
  const selectedTag = extendedProps?.datapoints?.[0] || datapoints?.[0] || {};
  const showAlias = Boolean(extendedProps?.showAlias);
  const selectedTagName = showAlias
    ? (
        selectedTag?.alias ||
        selectedTag?.datapointName ||
        selectedTag?.tagname ||
        selectedTag?.name ||
        extendedProps?.title ||
        ''
      )
    : (
        selectedTag?.datapointName ||
        selectedTag?.tagname ||
        selectedTag?.name ||
        extendedProps?.title ||
        selectedTag?.alias ||
        ''
      );
  const selectedTagType =
    selectedTag?.tagType ||
    selectedTag?.tagtype ||
    selectedTag?.dataType ||
    selectedTag?.datatype ||
    selectedTag?.type ||
    '';
  const selectedTagLabel = selectedTagType
    ? `${selectedTagName} (${selectedTagType})`
    : selectedTagName;
  const min = extendedProps?.min ?? 0;
  const max = extendedProps?.max ?? 100;
  const precision = normalizePrecision(extendedProps?.valuePrecision ?? 2);
  const numericMinRaw = Number(min);
  const numericMaxRaw = Number(max);
  const numericValue = Number(rawValue);
  const fallbackMin = 0;
  const fallbackMax = 100;
  const numericMin = isFinite(numericMinRaw) ? numericMinRaw : fallbackMin;
  const numericMax = isFinite(numericMaxRaw) ? numericMaxRaw : fallbackMax;
  const safeAxisMin = numericMin;
  const safeAxisMax = numericMax > numericMin ? numericMax : numericMin + 1;
  const safeValue = isFinite(numericValue) ? numericValue : (extendedProps?.value ?? 70);
  const range = numericMax - numericMin;
  const safeRange = isFinite(range) && range !== 0 ? range : 1;
  const rawNormalized = isFinite(Number(safeValue))
    ? (Number(safeValue) - numericMin) / safeRange
    : 0;
  const normalizedProgress = Math.min(1, Math.max(0, rawNormalized));
  return {
    backgroundColor: basicProps?.backgroundColor,
    graphic: selectedTagLabel
      ? [
          {
            type: 'text',
            left: 'center',
            bottom: 0,
            style: {
              text: selectedTagLabel,
              fill: extendedProps?.titleColor || '#999',
              font: `${extendedProps?.titleFontSize ?? 14}px Roboto`,
            },
          },
        ]
      : [],
    grid: {
      left: '5%',
      right: '5%',
      top: '20%',
      bottom: '20%',
      containLabel: true
    },
    xAxis: {
      type: 'value',
      min: safeAxisMin,
      max: safeAxisMax,
      axisLabel: {
        formatter: (value) => formatValue(value, precision),
        color: extendedProps?.axisLabelColor || '#999',
        fontSize: extendedProps?.axisLabelFontSize ?? 12
      },
      splitLine: { show: extendedProps?.showSplitLine ?? true },
      axisLine: { show: true }
    },
    yAxis: {
      type: 'category',
      data: [extendedProps?.title || 'Value'],
      axisLabel: {
        color: extendedProps?.yAxisLabelColor || '#999',
        fontSize: extendedProps?.yAxisLabelFontSize ?? 14
      }
    },
    tooltip: {
      trigger: 'item',
      formatter: (params) => {
        const valueText = formatValue(params?.value, precision);
        const tagText = selectedTagLabel || selectedTagName || 'Value';
        return `${tagText}<br/>Value: ${valueText}`;
      },
    },
    series: [{
      type: 'bar',
      data: [safeValue],
      barWidth: extendedProps?.barWidth || '40%',
      itemStyle: {
        color: extendedProps?.barColor || {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 1,
          y2: 0,
          colorStops: [
            { offset: 0, color: '#67e0e3' },
            { offset: normalizedProgress * 0.7, color: '#37a2da' },
            { offset: normalizedProgress, color: '#fd666d' }
          ]
        },
        borderRadius: extendedProps?.borderRadius || [0, 4, 4, 0]
      },
      label: {
        show: extendedProps?.showLabel ?? true,
        position: 'right',
        formatter: (params) => formatValue(params.value, precision),
        color: extendedProps?.labelColor || '#fff',
        fontSize: extendedProps?.labelFontSize ?? 16
      }
    }]
  };
};

registry['Speed Gauge'] = (basicProps, extendedProps, datapoints) => {
  const value = (Array.isArray(datapoints) && datapoints.length) ? (datapoints[0]?.value ?? datapoints[0]?.y ?? 0) : (extendedProps?.value ?? 80);
  const selectedTag = extendedProps?.datapoints?.[0] || datapoints?.[0] || {};
  const selectedTagName =
    selectedTag?.datapointName ||
    selectedTag?.tagname ||
    selectedTag?.name ||
    extendedProps?.title ||
    '';
  const selectedTagType =
    selectedTag?.tagType ||
    selectedTag?.tagtype ||
    selectedTag?.dataType ||
    selectedTag?.datatype ||
    selectedTag?.type ||
    '';
  const selectedTagLabel = selectedTagType
    ? `${selectedTagName} (${selectedTagType})`
    : selectedTagName;
  const min = extendedProps?.min ?? 0;
  const max = extendedProps?.max ?? 200;
  const precision = normalizePrecision(extendedProps?.valuePrecision ?? 2);
  return {
    backgroundColor: basicProps?.backgroundColor,
    graphic: selectedTagLabel
      ? [
          {
            type: 'text',
            left: 'center',
            bottom: 0,
            style: {
              text: selectedTagLabel,
              fill: extendedProps?.titleColor || '#999',
              font: `${extendedProps?.titleFontSize ?? 14}px Roboto`,
            },
          },
        ]
      : [],
    grid: {
      left: '5%',
      right: '5%',
      top: '5%',
      bottom: '5%',
      containLabel: false
    },
    series: [{
      type: 'gauge',
      startAngle: 225,
      endAngle: -45,
      min,
      max,
      radius: '85%',
      center: ['50%', '55%'],
      splitNumber: extendedProps?.splitNumber ?? 10,
      axisLine: {
        lineStyle: {
          width: extendedProps?.axisLineWidth ?? 8,
          color: extendedProps?.axisLineColor || [
            [0.2, '#91cc75'],
            [0.8, '#fac858'],
            [1, '#ee6666']
          ]
        }
      },
      pointer: {
        icon: 'path://M12.8,0.7l12,40.1H0.7L12.8,0.7z',
        length: extendedProps?.pointerLength || '70%',
        width: extendedProps?.pointerWidth ?? 10,
        offsetCenter: [0, '-60%'],
        itemStyle: {
          color: extendedProps?.pointerColor || 'auto'
        }
      },
      axisTick: {
        length: extendedProps?.axisTickLength ?? 8,
        lineStyle: {
          color: extendedProps?.axisTickColor || 'auto',
          width: 1
        }
      },
      splitLine: {
        length: extendedProps?.splitLineLength ?? 12,
        lineStyle: {
          color: extendedProps?.splitLineColor || 'auto',
          width: 3
        }
      },
      axisLabel: {
        distance: extendedProps?.axisLabelDistance ?? 18,
        color: extendedProps?.axisLabelColor || '#999',
        fontSize: extendedProps?.axisLabelFontSize ?? 12,
        formatter: (value) => {
          if (value === max) return formatValue(value, precision);
          return value % (extendedProps?.labelStep ?? 20) === 0 ? formatValue(value, precision) : '';
        }
      },
      title: {
        show: extendedProps?.showTitle ?? true,
        offsetCenter: extendedProps?.titleOffsetCenter || [0, '80%'],
        fontSize: extendedProps?.titleFontSize ?? 16,
        color: extendedProps?.titleColor || '#999'
      },
      detail: {
        valueAnimation: true,
        fontSize: extendedProps?.detailFontSize ?? 40,
        offsetCenter: extendedProps?.detailOffsetCenter || [0, '50%'],
        formatter: (value) => formatValue(value, precision),
        color: extendedProps?.detailColor || 'auto'
      },
      data: [{ value, name: '' }]
    }]
  };
};

registry['Temperature Gauge'] = (basicProps, extendedProps, datapoints) => {
  const value = (Array.isArray(datapoints) && datapoints.length) ? (datapoints[0]?.value ?? datapoints[0]?.y ?? 0) : (extendedProps?.value ?? 25);
  const selectedTag = extendedProps?.datapoints?.[0] || datapoints?.[0] || {};
  const selectedTagName =
    selectedTag?.datapointName ||
    selectedTag?.tagname ||
    selectedTag?.name ||
    extendedProps?.title ||
    '';
  const selectedTagType =
    selectedTag?.tagType ||
    selectedTag?.tagtype ||
    selectedTag?.dataType ||
    selectedTag?.datatype ||
    selectedTag?.type ||
    '';
  const selectedTagLabel = selectedTagType
    ? `${selectedTagName} (${selectedTagType})`
    : selectedTagName;
  const min = extendedProps?.min ?? -20;
  const max = extendedProps?.max ?? 60;
  const precision = normalizePrecision(extendedProps?.valuePrecision ?? 2);
  
  return {
    backgroundColor: basicProps?.backgroundColor,
    graphic: selectedTagLabel
      ? [
          {
            type: 'text',
            left: 'center',
            bottom: 0,
            style: {
              text: selectedTagLabel,
              fill: extendedProps?.titleColor || '#999',
              font: `${extendedProps?.titleFontSize ?? 14}px Roboto`,
            },
          },
        ]
      : [],
    grid: {
      left: '5%',
      right: '5%',
      top: '5%',
      bottom: '5%',
      containLabel: false
    },
    series: [{
      type: 'gauge',
      startAngle: 180,
      endAngle: 0,
      min,
      max,
      radius: '90%',
      center: ['50%', '80%'],
      axisLine: {
        lineStyle: {
          width: extendedProps?.axisLineWidth ?? 15,
          color: extendedProps?.axisLineColor || [
            [0.25, '#5470c6'],
            [0.5, '#91cc75'],
            [0.75, '#fac858'],
            [1, '#ee6666']
          ]
        }
      },
      pointer: {
        itemStyle: {
          color: extendedProps?.pointerColor || '#ee6666'
        },
        length: extendedProps?.pointerLength || '70%',
        width: extendedProps?.pointerWidth ?? 4
      },
      axisTick: {
        distance: extendedProps?.axisTickDistance ?? -15,
        length: extendedProps?.axisTickLength ?? 6,
        lineStyle: {
          color: extendedProps?.axisTickColor || '#fff',
          width: 1
        }
      },
      splitLine: {
        distance: extendedProps?.splitLineDistance ?? -15,
        length: extendedProps?.splitLineLength ?? 15,
        lineStyle: {
          color: extendedProps?.splitLineColor || '#fff',
          width: 3
        }
      },
      axisLabel: {
        distance: extendedProps?.axisLabelDistance ?? -45,
        color: extendedProps?.axisLabelColor || '#999',
        fontSize: extendedProps?.axisLabelFontSize ?? 12,
        formatter: (value) => formatValue(value, precision) + '°'
      },
      title: {
        show: false
      },
      detail: {
        valueAnimation: true,
        formatter: (value) => formatValue(value, precision) + '°C',
        color: extendedProps?.detailColor || 'auto',
        fontSize: extendedProps?.detailFontSize ?? 30,
        offsetCenter: extendedProps?.detailOffsetCenter || [0, '-20%']
      },
      data: [{ value, name: '' }]
    }]
  };
};

// Timeseries Table
registry['Timeseries Table'] = (basicProps, extendedProps, datapoints) => {
  // Use ECharts bar chart for table-like visualization
  const hasData = Array.isArray(datapoints) && datapoints.length > 0;
  const rows = hasData
    ? datapoints.map((dp, idx) => {
        const rawValue = dp.value !== undefined ? dp.value : (dp.y !== undefined ? dp.y : (dp.val !== undefined ? dp.val : 0));
        const numericValue = typeof rawValue === 'number' ? rawValue : parseFloat(rawValue);
        const parsedValue = Number.isFinite(numericValue) ? numericValue : 0;
        const rawTimestamp = dp.timestamp ?? dp.x ?? dp.time;
        const ts = parseTimestamp(rawTimestamp);
        const formattedTimestamp = ts !== null
          ? new Date(ts).toLocaleString()
          : (rawTimestamp || new Date().toLocaleString());
        const rowTextColor = dp.textColor || basicProps?.textColor || '#3C3C3C';
        const rowBgColor = dp.backgroundColor || dp.itemStyle?.color || dp.color || '#008CD7';

        return {
          category: dp.datapointName || dp.alias || dp.name || dp.tagid || dp.id || `Point ${idx + 1}`,
          value: parsedValue,
          timestamp: formattedTimestamp,
          textColor: rowTextColor,
          bgColor: rowBgColor,
        };
      })
    : [{
        category: 'No Data',
        value: 0,
        timestamp: '-',
        textColor: basicProps?.textColor || '#3C3C3C',
        bgColor: '#008CD7',
      }];

  const categories = rows.map((row) => row.category);
  const values = rows.map((row) => row.value);
  const timestamps = rows.map((row) => row.timestamp);
  const seriesData = rows.map((row) => ({
    value: row.value,
    itemStyle: {
      color: row.bgColor,
      borderRadius: [4, 4, 0, 0],
    },
    label: {
      color: row.textColor,
    },
  }));

  return {
    backgroundColor: basicProps?.backgroundColor || 'transparent',
    grid: { 
      top: 50, 
      right: 30, 
      bottom: 80, 
      left: 50, 
      containLabel: true 
    },
    xAxis: {
      type: 'category',
      data: categories,
      axisLabel: {
        color: basicProps?.textColor || '#3C3C3C',
        fontSize: (basicProps?.fontSize || 12) - 1,
        interval: 0,
        rotate: 45,
        formatter: function(value) {
          return value && value.length > 15 ? value.substring(0, 15) + '...' : value;
        }
      },
      axisLine: { lineStyle: { color: '#E5E9E8' } },
      axisTick: { show: true }
    },
    yAxis: {
      type: 'value',
      name: 'Value',
      nameLocation: 'middle',
      nameGap: 45,
      nameTextStyle: {
        color: basicProps?.textColor || '#3C3C3C',
        fontSize: basicProps?.fontSize || 12,
        fontWeight: 500
      },
      axisLabel: {
        color: basicProps?.textColor || '#3C3C3C',
        fontSize: (basicProps?.fontSize || 12) - 1
      },
      axisLine: { show: true, lineStyle: { color: '#E5E9E8' } },
      splitLine: { lineStyle: { color: '#F5F5F5', type: 'dashed' } }
    },
    series: [{
      type: 'bar',
      data: seriesData,
      barMaxWidth: 50,
      label: {
        show: true,
        position: 'top',
        formatter: (params) => `${params?.value ?? 0}`,
        color: basicProps?.textColor || '#3C3C3C',
        fontSize: (basicProps?.fontSize || 12) - 1
      },
      emphasis: {
        itemStyle: {
          color: '#005C8E'
        }
      }
    }],
    tooltip: {
      trigger: 'item',
      confine: true,
      formatter: function(params) {
        if (params && params.dataIndex !== undefined && hasData) {
          const idx = params.dataIndex;
          const row = rows[idx] || {};
          return `<b>${categories[idx]}</b><br/>Timestamp: ${timestamps[idx]}<br/>Value: ${values[idx]}<br/>Text Color: ${row.textColor || '-'}<br/>Background Color: ${row.bgColor || '-'}`;
        }
        return 'No data1234567';
      }
    }
  };
};

// Sankey Chart - Using echarts-for-react (matching Graphs/Sankey/Sankey.js)
registry['Sankey Chart'] = (basicProps, extendedProps, datapoints) => {
  // This chart uses linkageObject structure from Redux state
  // Format: { key: { associated_value: number, children: [child1, child2] }, ... }
  // Also supports simple format: [{ source, target, value }, ...]
  
  const hasData = Array.isArray(datapoints) && datapoints.length > 0;
  const hasLinkageObject = extendedProps?.linkageObject && typeof extendedProps.linkageObject === 'object' && Object.keys(extendedProps.linkageObject).length > 0;
  
  let data = [];
  let links = [];
  const datapointStyles = Array.isArray(extendedProps?.datapoints) ? extendedProps.datapoints : [];
  const datapointColorMap = new Map();
  datapointStyles.forEach((dp) => {
    const key = dp?.datapointName || dp?.tagname || dp?.name || dp?.title || dp?.tagid || dp?.key;
    const color = dp?.itemStyle?.color || dp?.color || dp?.backgroundColor;
    if (key && color) datapointColorMap.set(String(key), color);
  });
  const resolveNodeColor = (nodeKey, fallbackColor) => {
    if (nodeKey && datapointColorMap.has(String(nodeKey))) {
      return datapointColorMap.get(String(nodeKey));
    }
    return fallbackColor;
  };
  
  if (hasLinkageObject) {
    // MAIN PATH: Handle linkageObject format (from Redux tagcollectionvalues)
    const linkageObject = extendedProps.linkageObject;
    const colorMap = new Map();
    const nodesSet = new Set();
    const precision = extendedProps?.valuePrecision ?? 2;
    
    // Build color map from tagprops (matching actual implementation)
    if (Array.isArray(extendedProps?.tagprops)) {
      extendedProps.tagprops.forEach(tag => {
        if (tag.tagid && tag.chartprops?.color) {
          colorMap.set(tag.tagid, tag.chartprops.color);
        }
      });
    }
    
    // Generate nodes and links from linkageObject (exactly as Graphs/Sankey/Sankey.js)
    for (const [key, value] of Object.entries(linkageObject)) {
      const nodeValue = Number(value.associated_value ?? 0).toFixed(precision);
      const nodeName = `${key}: ${nodeValue} kWh`;
      
      // Add parent node if not already added
      if (!nodesSet.has(key)) {
        nodesSet.add(key);
        data.push({
          name: nodeName,
          value: nodeValue,
          itemStyle: {
              color: resolveNodeColor(key, colorMap.get(key) || getColor(basicProps, extendedProps)),
            opacity: 1
          }
        });
      }
      
      // Add child nodes and create links
      if (Array.isArray(value.children)) {
        value.children.forEach(child => {
          const childValue = linkageObject[child];
          if (childValue) {
            const childNodeValue = Number(childValue.associated_value ?? 0).toFixed(precision);
            const childNodeName = `${child}: ${childNodeValue} kWh`;
            
            // Add child node if not already added
            if (!nodesSet.has(child)) {
              nodesSet.add(child);
              data.push({
                name: childNodeName,
                value: childNodeValue,
                itemStyle: {
                  color: resolveNodeColor(child, colorMap.get(child) || getColor(basicProps, extendedProps)),
                  opacity: 1
                }
              });
            }
            
            // Create link from parent to child (matching actual implementation)
            links.push({
              source: nodeName,
              target: childNodeName,
              value: Number(childNodeValue),
              lineStyle: {
                opacity: extendedProps?.lineOpacity ?? 0.2,
                curveness: extendedProps?.curveness ?? 0.5
              }
            });
          }
        });
      }
    }
  } else if (hasData) {
    // FALLBACK: Handle simple source-target-value format
    const nodeSet = new Set();
    const colorMap = new Map();
    const precision = extendedProps?.valuePrecision ?? 2;
    
    // Build color map if tagprops provided
    if (Array.isArray(extendedProps?.tagprops)) {
      extendedProps.tagprops.forEach(tag => {
        if (tag.tagid && tag.chartprops?.color) {
          colorMap.set(tag.tagid, tag.chartprops.color);
        }
      });
    }
    
    datapoints.forEach(dp => {
      if (dp.source && dp.target && dp.value !== undefined) {
        const sourceValue = Number(dp.sourceValue ?? dp.value ?? 0).toFixed(precision);
        const targetValue = Number(dp.targetValue ?? dp.value ?? 0).toFixed(precision);
        const sourceName = `${dp.source}: ${sourceValue}`;
        const targetName = `${dp.target}: ${targetValue}`;
        
        if (!nodeSet.has(dp.source)) {
          nodeSet.add(dp.source);
          data.push({
            name: sourceName,
            value: sourceValue,
            itemStyle: {
              color: resolveNodeColor(dp.source, colorMap.get(dp.source) || dp?.itemStyle?.color || dp?.color || getColor(basicProps, extendedProps)),
              opacity: 1
            }
          });
        }
        
        if (!nodeSet.has(dp.target)) {
          nodeSet.add(dp.target);
          data.push({
            name: targetName,
            value: targetValue,
            itemStyle: {
              color: resolveNodeColor(dp.target, colorMap.get(dp.target) || dp?.itemStyle?.color || dp?.color || getColor(basicProps, extendedProps)),
              opacity: 1
            }
          });
        }
        
        links.push({
          source: sourceName,
          target: targetName,
          value: Number(dp.value) || 0,
          lineStyle: {
            opacity: extendedProps?.lineOpacity ?? 0.2,
            curveness: extendedProps?.curveness ?? 0.5
          }
        });
      }
    });

    // If tags are provided without explicit source/target, create a valid Sankey flow
    if (links.length === 0 && Array.isArray(datapoints) && datapoints.length > 0) {
      const rootLabel = extendedProps?.rootNodeName || 'Total';
      const rootName = `${rootLabel}: 0`;
      const rootColor = extendedProps?.rootNodeColor || getColor(basicProps, extendedProps);
      let rootSum = 0;

      data.push({
        name: rootName,
        value: 0,
        itemStyle: {
          color: rootColor,
          opacity: 1,
        },
      });

      datapoints.forEach((dp, idx) => {
        const nodeKey = dp.datapointName || dp.tagname || dp.name || dp.title || `Node ${idx + 1}`;
        const parsedValue = Number(dp.value ?? dp.y ?? dp.val ?? 1);
        const flowValue = Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 1;
        rootSum += flowValue;
        const nodeName = `${nodeKey}: ${Number(flowValue).toFixed(precision)}`;

        data.push({
          name: nodeName,
          value: Number(flowValue).toFixed(precision),
          itemStyle: {
            color: resolveNodeColor(nodeKey, dp?.itemStyle?.color || dp?.color || getColor(basicProps, extendedProps)),
            opacity: 1,
          },
        });

        links.push({
          source: rootName,
          target: nodeName,
          value: flowValue,
          lineStyle: {
            opacity: extendedProps?.lineOpacity ?? 0.2,
            curveness: extendedProps?.curveness ?? 0.5,
          },
        });
      });

      data[0] = {
        ...data[0],
        name: `${rootLabel}: ${Number(rootSum).toFixed(precision)}`,
        value: Number(rootSum).toFixed(precision),
      };
    }
  } else {
    // DEMO: Default data for preview
    const precision = extendedProps?.valuePrecision ?? 2;
    data = [
      { name: 'Source A: 100.00 kWh', value: '100.00', itemStyle: { color: '#5470C6', opacity: 1 } },
      { name: 'Source B: 80.00 kWh', value: '80.00', itemStyle: { color: '#91419F', opacity: 1 } },
      { name: 'Middle: 120.00 kWh', value: '120.00', itemStyle: { color: '#EE6666', opacity: 1 } },
      { name: 'Target 1: 90.00 kWh', value: '90.00', itemStyle: { color: '#73C0DE', opacity: 1 } },
      { name: 'Target 2: 30.00 kWh', value: '30.00', itemStyle: { color: '#3CB371', opacity: 1 } }
    ];
    
    links = [
      { source: 'Source A: 100.00 kWh', target: 'Middle: 120.00 kWh', value: 100, lineStyle: { opacity: 0.2, curveness: 0.5 } },
      { source: 'Source B: 80.00 kWh', target: 'Middle: 120.00 kWh', value: 80, lineStyle: { opacity: 0.2, curveness: 0.5 } },
      { source: 'Middle: 120.00 kWh', target: 'Target 1: 90.00 kWh', value: 90, lineStyle: { opacity: 0.2, curveness: 0.5 } },
      { source: 'Middle: 120.00 kWh', target: 'Target 2: 30.00 kWh', value: 30, lineStyle: { opacity: 0.2, curveness: 0.5 } }
    ];
  }
  
  // Build the exact ECharts option object (matching Graphs/Sankey/Sankey.js structure)
  return {
    tooltip: {
      backgroundColor: extendedProps?.tooltipBg || '#005C8E',
      borderColor: extendedProps?.tooltipBorderColor || '#005C8E',
      extraCssText: 'border-radius: 0 0 0 8px',
      valueFormatter: (value) => Number(value).toFixed(extendedProps?.valuePrecision ?? 2),
      textStyle: {
        color: extendedProps?.tooltipTextColor || '#fff',
        fontSize: extendedProps?.tooltipFontSize ?? 12,
        height: 3,
        fontFamily: extendedProps?.tooltipFontFamily || 'Roboto',
        padding: 0
      }
    },
    series: [{
      type: 'sankey',
      layout: extendedProps?.layout || 'none',
      left: extendedProps?.left || '2.5%',
      top: extendedProps?.top || '8%',
      right: extendedProps?.right || '10%',
      bottom: extendedProps?.bottom || '1%',
      nodeWidth: extendedProps?.nodeWidth ?? 10,
      nodeGap: extendedProps?.nodeGap ?? 13,
      nodeAlign: extendedProps?.nodeAlign || 'left',
      emphasis: {
        focus: 'adjacency'
      },
      data: data,
      links: links,
      lineStyle: {
        color: extendedProps?.lineStyleColor || 'source',
        opacity: extendedProps?.lineOpacity ?? 0.2,
        curveness: extendedProps?.curveness ?? 0.5
      },
      itemStyle: {
        borderColor: extendedProps?.itemBorderColor || '#aaa',
        borderWidth: extendedProps?.itemBorderWidth ?? 1,
        opacity: 1
      },
      label: {
        show: extendedProps?.showLabel ?? true,
        position: extendedProps?.labelPosition || 'right',
        distance: extendedProps?.labelDistance ?? 5,
        color: extendedProps?.label_color || extendedProps?.labelColor || '#000',
        fontWeight: extendedProps?.fontweight || extendedProps?.fontWeight || 400,
        fontSize: extendedProps?.fontsize || extendedProps?.fontSize || 12,
        fontFamily: extendedProps?.fontfamily || extendedProps?.fontFamily || 'Roboto',
        align: extendedProps?.labelAlign || 'left',
        borderWidth: 0
      },
      layoutIterations: extendedProps?.layoutIterations ?? 32
    }]
  };
};

// Funnel Chart
registry['Funnel'] = (basicProps, extendedProps, datapoints) => {
  // Funnel charts show progression through stages with visual width reduction
  // Expected datapoints format: [{ name: 'Stage1', value: 100, tagid: 'tag1' }, ...]
  const hasData = Array.isArray(datapoints) && datapoints.length > 0;
  const precision = extendedProps?.valuePrecision ?? 2;
  
  let funnelData = [];
  if (hasData) {
    // Process and sort datapoints
    funnelData = datapoints.map((dp, idx) => ({
      label: dp.datapointName || dp.alias || dp.name || dp.label || `Stage ${idx + 1}`,
      value: Number(dp.value ?? dp.y ?? 0),
      tagid: dp.tagid || dp.id || `tag_${idx}`,
      color: dp.itemStyle?.color || dp.color
    })).filter(item => item.value >= 0);
    
    // Sort based on energyConsumerSaver flag (true = descending for energy, false = ascending)
    const isEnergySaver = extendedProps?.energyConsumerSaver ?? true;
    funnelData.sort((a, b) => isEnergySaver ? b.value - a.value : a.value - b.value);
  } else {
    // Default demo data
    funnelData = [
      { label: 'Stage 1', value: 100, tagid: 'stage_1' },
      { label: 'Stage 2', value: 80, tagid: 'stage_2' },
      { label: 'Stage 3', value: 60, tagid: 'stage_3' },
      { label: 'Stage 4', value: 40, tagid: 'stage_4' },
      { label: 'Stage 5', value: 20, tagid: 'stage_5' }
    ];
  }
  
  // Dynamic funnel colors based on energyConsumerSaver
  const isEnergySaver = extendedProps?.energyConsumerSaver ?? true;
  const defaultColors = isEnergySaver 
    ? ["#E24A4F", "#E56367", "#EA7D81", "#EF989B", "#F2B1B3", "#F7C6C8", "#FAD9DB", "#FDECEC"]
    : ["#29A55C", "#47B273", "#66BF8B", "#85CCA2", "#A3D8B9", "#BFE5CF", "#D5F2E0", "#EBFFF1"];
  
  const funnelColors = extendedProps?.funnelColors || defaultColors;
  
  // Calculate widths - largest to smallest
  const maxWidth = extendedProps?.maxWidth || 401;
  const minWidth = extendedProps?.minWidth || 80;
  const stepCount = funnelData.length;
  const widthStep = stepCount > 1 ? (maxWidth - minWidth) / (stepCount - 1) : 0;
  
  // ECharts visualization with custom data for positioning
  const seriesData = funnelData.map((step, idx) => ({
    value: step.value,
    name: `${step.label}: ${step.value.toFixed(precision)}`,
    itemStyle: {
      color: step.color || funnelColors[idx % funnelColors.length],
      opacity: extendedProps?.opacity ?? 1
    }
  }));
  
  return {
    backgroundColor: basicProps?.backgroundColor,
    tooltip: {
      trigger: 'item',
      confine: true,
      formatter: (params) => {
        const percent = params.percent || 0;
        return `${params.name}<br/>Percentage: ${percent.toFixed(1)}%`;
      },
      textStyle: {
        color: extendedProps?.tooltipTextColor || '#fff',
        fontSize: extendedProps?.tooltipFontSize || 12
      },
      backgroundColor: extendedProps?.tooltipBg || 'rgba(0, 0, 0, 0.7)',
      borderColor: extendedProps?.tooltipBorder || '#333'
    },
    series: [{
      type: 'funnel',
      data: seriesData,
      sort: extendedProps?.sort || 'descending',
      gap: extendedProps?.gap ?? 10,
      label: {
        show: extendedProps?.showLabel ?? true,
        position: extendedProps?.labelPosition || 'inside',
        fontSize: extendedProps?.labelFontSize ?? 12,
        color: extendedProps?.labelColor || '#EDEFEE',
        formatter: (params) => `${funnelData[params.dataIndex].label}:${params.value.toFixed(precision)}`,
        lineHeight: 31,
        textAlign: 'center',
        overflow: 'ellipsis',
        whiteSpace: 'nowrap'
      },
      labelLine: {
        show: extendedProps?.showLabelLine ?? false
      },
      itemStyle: {
        borderColor: extendedProps?.itemBorderColor || '#fff',
        borderWidth: extendedProps?.itemBorderWidth ?? 1,
        borderRadius: extendedProps?.borderRadius ?? 16,
        opacity: extendedProps?.itemOpacity ?? 1
      },
      emphasis: {
        itemStyle: {
          opacity: extendedProps?.emphasisOpacity ?? 1.0
        },
        label: {
          fontSize: extendedProps?.emphasisLabelFontSize ?? 14
        }
      },
      left: extendedProps?.funnelLeft || 'center',
      right: extendedProps?.funnelRight || 'auto',
      top: extendedProps?.funnelTop || 'auto',
      bottom: extendedProps?.funnelBottom || 'auto',
      width: extendedProps?.funnelWidth || '80%',
      height: extendedProps?.funnelHeight || '100%',
      minSize: extendedProps?.minSize || '0%',
      maxSize: extendedProps?.maxSize || '100%',
      funnelAlign: extendedProps?.funnelAlign || 'center'
    }]
  };
};

// Mixed Chart (Line + Bar combination)
registry['Mixed Chart'] = (basicProps, extendedProps, datapoints) => {
  const { categories, values } = extractXY(datapoints);
  const precision = extendedProps?.valuePrecision ?? 2;
  
  // Split data: first half as bar, second half as line
  const midpoint = Math.ceil(values.length / 2);
  const barData = values.slice(0, midpoint);
  const lineData = values.slice(midpoint);
  
  return {
    color: [getColor(basicProps, extendedProps)],
    backgroundColor: basicProps?.backgroundColor,
    grid: {
      left: '10%',
      right: '10%',
      bottom: '90',
      top: '20',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: categories,
      name: extendedProps?.xAxisLabel || 'Category',
      nameLocation: 'middle',
      nameGap: 30,
      nameTextStyle: { fontWeight: 500, color: basicProps?.textColor || '#3C3C3C' },
      axisLabel: { 
        margin: 6, 
        color: '#56636E',
        formatter: (value) => formatTimeLabel(value)
      },
      splitLine: { show: !!extendedProps?.showGridLines }
    },
    yAxis: {
      type: 'value',
      name: extendedProps?.yAxisLabel || 'Value',
      nameLocation: 'middle',
      nameGap: 65,
      nameRotate: 90,
      nameTextStyle: { fontWeight: 500, color: basicProps?.textColor || '#3C3C3C' },
      axisLabel: { 
        margin: 10, 
        color: '#56636E',
        formatter: (value) => formatKWh(value, precision)
      },
      splitLine: { show: !!extendedProps?.showGridLines }
    },
    series: [
      {
        type: 'bar',
        data: barData,
        barWidth: extendedProps?.barWidth || '60%',
        itemStyle: { color: extendedProps?.barColor || '#5470C6' },
        name: extendedProps?.barSeriesName || 'Bar Series'
      },
      {
        type: 'line',
        data: lineData,
        smooth: extendedProps?.smooth ?? true,
        itemStyle: { color: extendedProps?.lineColor || '#EE6666' },
        lineStyle: { width: extendedProps?.lineWidth || 2 },
        name: extendedProps?.lineSeriesName || 'Line Series'
      }
    ],
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        const rows = Array.isArray(params) ? params : [params];
        if (!rows.length) return '';
        const rawHeader = rows[0]?.axisValueLabel || rows[0]?.name || '';
        const header = formatTimeLabel(rawHeader);
        const lines = rows.map((p) => {
          const seriesName = p?.seriesName || p?.name || 'Series';
          const rawValue = Array.isArray(p?.value)
            ? p.value[p.value.length - 1]
            : p?.value;
          return `${p?.marker || ''}${seriesName}: ${formatKWh(rawValue, precision)}`;
        });
        return header ? `${header}<br/>${lines.join('<br/>')}` : lines.join('<br/>');
      }
    },
    legend: {
      show: extendedProps?.showLegend ?? true,
      orient: 'horizontal',
      left: 'center',
      bottom: 0
    }
  };
};

// Treemap Chart
registry['Treemap'] = (basicProps, extendedProps, datapoints) => {
  const precision = extendedProps?.valuePrecision ?? 2;
  const hasData = Array.isArray(datapoints) && datapoints.length > 0;
  
  let treemapData = [];
  if (hasData) {
    treemapData = datapoints.map((dp, idx) => ({
      name: dp.datapointName || dp.alias || dp.name || `Item ${idx + 1}`,
      value: Number(dp.value ?? dp.y ?? 0),
      itemStyle: dp.itemStyle?.color || dp.color ? { color: dp.itemStyle?.color || dp.color } : undefined,
      children: Array.isArray(dp.children) ? dp.children.map((child, childIdx) => ({
        name: child.name || `Sub-Item ${childIdx + 1}`,
        value: Number(child.value ?? 0)
      })) : []
    }));
  } else {
    // Default demo data
    treemapData = [
      { name: 'Category A', value: 100, children: [
        { name: 'Item A1', value: 60 },
        { name: 'Item A2', value: 40 }
      ]},
      { name: 'Category B', value: 80, children: [
        { name: 'Item B1', value: 50 },
        { name: 'Item B2', value: 30 }
      ]}
    ];
  }
  
  return {
    backgroundColor: basicProps?.backgroundColor,
    tooltip: {
      trigger: 'item',
      formatter: (params) => `${params.name}<br/>Value: ${formatKWh(params.value, precision)}`
    },
    series: [{
      type: 'treemap',
      data: treemapData,
      roam: extendedProps?.roam ?? true,
      nodeClick: extendedProps?.nodeClick || false,
      label: {
        show: extendedProps?.showLabel ?? true,
        fontSize: extendedProps?.labelFontSize ?? 12,
        color: extendedProps?.labelColor || '#000'
      },
      itemStyle: {
        borderColor: extendedProps?.borderColor || '#fff',
        borderWidth: extendedProps?.borderWidth ?? 1
      },
      breadcrumb: {
        show: extendedProps?.showBreadcrumb ?? false,
        textStyle: { color: basicProps?.textColor || '#3C3C3C' }
      }
    }]
  };
};

// Linear Progress Bar Chart
registry['Linear Progress Bar'] = (basicProps, extendedProps, datapoints) => {
  const hasData = Array.isArray(datapoints) && datapoints.length > 0;
  const maxValue = extendedProps?.maxValue ?? 100;
  
  let progressData = [];
  if (hasData) {
    progressData = datapoints.map((dp, idx) => {
      const name = dp.datapointName || dp.alias || dp.name || `Progress ${idx + 1}`;
      const value = Number(dp.value ?? dp.y ?? 0);
      return { x: name, y: value, color: dp.itemStyle?.color || dp.color };
    });
  } else {
    progressData = [
      { x: 'Process 1', y: 75 },
      { x: 'Process 2', y: 50 },
      { x: 'Process 3', y: 90 }
    ];
  }
  
  return {
    backgroundColor: basicProps?.backgroundColor,
    tooltip: {
      trigger: 'item',
      formatter: (params) => `${params.name}: ${params.value.toFixed(2)}%`
    },
    grid: {
      left: '15%',
      right: '10%',
      top: '10%',
      bottom: '10%',
      containLabel: true
    },
    xAxis: {
      type: 'value',
      max: maxValue,
      axisLabel: { show: true, color: '#56636E' },
      splitLine: { show: !!extendedProps?.showGridLines }
    },
    yAxis: {
      type: 'category',
      data: progressData.map(d => d.x),
      axisLabel: { color: basicProps?.textColor || '#3C3C3C' }
    },
    series: [{
      type: 'bar',
      data: progressData.map(d => d.y),
      barHeight: extendedProps?.barHeight || '40%',
      itemStyle: {
        color: (params) => progressData?.[params.dataIndex]?.color || extendedProps?.barColor || '#5470C6',
        borderRadius: extendedProps?.borderRadius ?? 4
      },
      label: {
        show: extendedProps?.showLabel ?? true,
        position: 'right',
        formatter: (params) => `${params.value.toFixed(1)}%`
      }
    }]
  };
};

// Solar Chart
registry['Solar Chart'] = (basicProps, extendedProps, datapoints) => {
  const hasData = Array.isArray(datapoints) && datapoints.length > 0;
  const precision = extendedProps?.valuePrecision ?? 2;
  
  let solarMetrics = {
    power: 0,
    energy: 0,
    efficiency: 0,
    temperature: 0
  };
  const selectedTagName = hasData
    ? (datapoints[0]?.datapointName || datapoints[0]?.name || datapoints[0]?.tagname || 'Solar')
    : (extendedProps?.title || 'Solar');
  const valueText = `${solarMetrics.efficiency.toFixed(precision)}%`;
  
  if (hasData) {
    // Extract first datapoint as solar metrics
    const firstData = datapoints[0];
    solarMetrics = {
      power: Number(firstData.power ?? firstData.value ?? 0),
      energy: Number(firstData.energy ?? 0),
      efficiency: Number(firstData.efficiency ?? firstData.y ?? 0),
      temperature: Number(firstData.temperature ?? 0)
    };
  } else {
    solarMetrics = {
      power: 2.5,
      energy: 12.4,
      efficiency: 85,
      temperature: 45
    };
  }
  
  const solarDetailFontSize = Number.isFinite(Number(extendedProps?.detailFontSize))
    ? Math.min(28, Math.max(14, Number(extendedProps?.detailFontSize)))
    : 22;
  const rawSolarRadius = extendedProps?.radius;
  const parsedSolarRadius =
    typeof rawSolarRadius === 'string'
      ? Number.parseFloat(rawSolarRadius.replace('%', ''))
      : Number(rawSolarRadius);
  const safeSolarRadius = Number.isFinite(parsedSolarRadius)
    ? Math.min(180, Math.max(90, parsedSolarRadius))
    : 140;

  return {
    backgroundColor: basicProps?.backgroundColor,
    graphic: [
      {
        type: 'text',
        left: 'center',
        top: '80%',
        style: {
          text: selectedTagName,
          fill: basicProps?.textColor || '#3C3C3C',
          fontSize: 13,
          fontWeight: 500,
          fontFamily: 'Roboto',
          textAlign: 'center'
        }
      },
      {
        type: 'text',
        left: 'center',
        top: '88%',
        style: {
          text: valueText,
          fill: basicProps?.textColor || '#3C3C3C',
          fontSize: 16,
          fontWeight: 700,
          fontFamily: 'Roboto',
          textAlign: 'center'
        }
      }
    ],
    tooltip: {
      trigger: 'item',
      formatter: (params) => `${params.name}: ${formatKWh(params.value, precision)}`
    },
    series: [{
      type: 'gauge',
      progress: { show: true, width: 14, itemStyle: { color: '#005C8E' } },
      axisLine: { lineStyle: { width: 24, color: [[1, '#E5E7EB']] } },
      axisTick: { distance: -30, length: 8, lineStyle: { color: '#fff', width: 2 } },
      splitLine: { distance: -30, length: 30, lineStyle: { color: '#fff', width: 4 } },
      axisLabel: { color: '#999', distance: 40, fontSize: 10 },
      detail: { valueAnimation: true, formatter: '{value}%', color: '#005C8E', fontSize: solarDetailFontSize, offsetCenter: [0, '-6%'] },
      data: [{ value: solarMetrics.efficiency, name: 'Efficiency' }]
      ,
      center: ['50%', '50%'],
      radius: `${safeSolarRadius}%`
    }]
  };
};

// BBT (Temperature Monitor) Chart
registry['BBT'] = (basicProps, extendedProps, datapoints) => {
  const hasData = Array.isArray(datapoints) && datapoints.length > 0;
  const precision = extendedProps?.valuePrecision ?? 2;
  
  let bbtData = [];
  if (hasData) {
    bbtData = datapoints.map((dp, idx) => ({
      sensorNumber: idx + 1,
      value: Number(dp.value ?? dp.y ?? 0),
      temperature: Number(dp.temperature ?? 0),
      timestamp: dp.timestamp || new Date().toLocaleString()
    }));
  } else {
    bbtData = [
      { sensorNumber: 1, value: 22.5, temperature: 22.5, timestamp: '19-02-2026 10:30:45' },
      { sensorNumber: 2, value: 23.1, temperature: 23.1, timestamp: '19-02-2026 10:30:46' },
      { sensorNumber: 3, value: 21.8, temperature: 21.8, timestamp: '19-02-2026 10:30:47' }
    ];
  }
  
  return {
    backgroundColor: basicProps?.backgroundColor,
    grid: {
      left: '15%',
      right: '10%',
      top: '10%',
      bottom: '15%',
      containLabel: true
    },
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        if (params.length) {
          return `Temperature: ${params[0].value.toFixed(2)}°C`;
        }
        return '';
      }
    },
    xAxis: {
      type: 'category',
      data: bbtData.map(d => `Sensor ${d.sensorNumber}`),
      axisLabel: { color: basicProps?.textColor || '#3C3C3C' }
    },
    yAxis: {
      type: 'value',
      name: 'Temperature (°C)',
      nameLocation: 'middle',
      nameGap: 50,
      nameTextStyle: { color: basicProps?.textColor || '#3C3C3C' },
      axisLabel: { color: '#56636E' }
    },
    series: [{
      type: 'bar',
      data: bbtData.map(d => d.value),
      itemStyle: {
        color: (params) => {
          const value = bbtData[params.dataIndex].value;
          if (value > 25) return '#DC272D'; // Red for high
          if (value > 22) return '#FFA500'; // Orange for medium
          return '#00943D'; // Green for normal
        }
      },
      label: {
        show: extendedProps?.showLabel ?? true,
        position: 'top',
        formatter: (params) => `${bbtData[params.dataIndex].value.toFixed(2)}°C`,
        color: basicProps?.textColor || '#3C3C3C'
      }
    }]
  };
};

// PQ Events Pie Chart (Power Quality Events Breakdown)
registry['PQ Events Pie'] = (basicProps, extendedProps, datapoints) => {
  // PQ events breakdown pie chart
  // Expected datapoints format: [{ label, value }, ...] or uses predefined PQ event labels
  // Or: [{ name, value }, ...]
  
  const hasData = Array.isArray(datapoints) && datapoints.length > 0;
  const precision = extendedProps?.valuePrecision ?? 2;
  
  // Predefined PQ event labels (matching actual implementation)
  const pqEventLabels = extendedProps?.pqEventLabels || [
    'Over Voltage',
    'Under Voltage',
    'Sags',
    'Swells',
    'Interruptions',
    'Transients'
  ];
  
  let pieData = [];
  let colorArray = [];
  
  if (hasData) {
    // Use provided datapoints
    pieData = datapoints.map((dp, idx) => ({
      value: Number(dp.value ?? dp.y ?? 0),
      name: dp.label || dp.datapointName || dp.alias || dp.name || pqEventLabels[idx] || `Event ${idx + 1}`
    })).filter(item => item.value >= 0);

    const datapointColors = datapoints
      .map((dp) => dp?.itemStyle?.color || dp?.color)
      .filter(Boolean);
    if (datapointColors.length) {
      colorArray = datapointColors;
    }
    
    // Build color array from tagprops if available
    if (Array.isArray(extendedProps?.tagprops)) {
      extendedProps.tagprops.forEach((tag, idx) => {
        if (tag.chartprops?.color) {
          colorArray.push(tag.chartprops.color);
        }
      });
    }
  } else {
    // Default PQ events data
    pieData = pqEventLabels.map((label, idx) => ({
      value: extendedProps?.demoValues?.[idx] ?? (idx === 2 ? 4 : (idx === 5 ? 1 : 0)),
      name: label
    }));
  }
  
  // Use provided colors or default palette
  if (colorArray.length === 0) {
    colorArray = extendedProps?.colors || [
      '#5470C6', '#91419F', '#EE6666', '#73C0DE', '#3CB371', '#FFA500',
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A'
    ];
  }
  
  // Filter to show only items with values (optional)
  const showZeroValues = extendedProps?.showZeroValues ?? true;
  const filteredData = showZeroValues ? pieData : pieData.filter(item => item.value > 0);
  
  return {
    backgroundColor: basicProps?.backgroundColor,
    color: colorArray,
    tooltip: {
      trigger: 'item',
      formatter: (params) => {
        const percent = params.percent || 0;
        return `${params.name}<br/>Value: ${Number(params.value).toFixed(precision)}<br/>Percentage: ${percent.toFixed(1)}%`;
      },
      textStyle: {
        color: extendedProps?.tooltipTextColor || '#000',
        fontSize: extendedProps?.tooltipFontSize || 12
      }
    },
    legend: {
      show: extendedProps?.showLegend ?? true,
      orient: extendedProps?.legendOrient || 'vertical',
      left: extendedProps?.legendLeft || 'left',
      top: extendedProps?.legendTop || 'center',
      textStyle: {
        color: basicProps?.textColor || '#333',
        fontSize: extendedProps?.legendFontSize || 12
      }
    },
    series: [{
      type: 'pie',
      data: filteredData,
      radius: extendedProps?.radius || '50%',
      label: {
        show: extendedProps?.showLabel ?? (extendedProps?.label?.show ?? true),
        position: extendedProps?.labelPosition || (extendedProps?.label?.position || 'outside'),
        formatter: extendedProps?.labelFormatter || '{b}: {c}',
        fontSize: extendedProps?.labelFontSize || 12,
        color: extendedProps?.labelColor || basicProps?.textColor || '#333'
      },
      emphasis: {
        itemStyle: {
          shadowBlur: extendedProps?.shadowBlur ?? 10,
          shadowOffsetX: 0,
          shadowOffsetY: 0,
          shadowColor: 'rgba(0, 0, 0, 0.5)',
          opacity: extendedProps?.emphasisOpacity ?? 1
        },
        label: {
          show: extendedProps?.emphasisLabelShow ?? true,
          fontSize: extendedProps?.emphasisLabelFontSize || 14
        }
      },
      itemStyle: {
        borderColor: extendedProps?.itemBorderColor || basicProps?.backgroundColor || '#fff',
        borderWidth: extendedProps?.itemBorderWidth ?? 2,
        borderRadius: extendedProps?.borderRadius ?? 0
      }
    }]
  };
};

// AQI Gauge: Semi-circular gauge with color ranges (0-500 scale)
registry['AQI Gauge'] = (basicProps, extendedProps, datapoints) => {
  const theme = basicProps?.theme || 'light';
  const gaugeValue = datapoints.length > 0 ? Number(datapoints[0]?.value ?? datapoints[0]?.y ?? 300) : 300;
  const minValue = extendedProps?.minValue ?? 0;
  const maxValue = extendedProps?.maxValue ?? 500;
  const rawAqiRadius = extendedProps?.radius;
  const parsedAqiRadius =
    typeof rawAqiRadius === 'string'
      ? Number.parseFloat(rawAqiRadius.replace('%', ''))
      : Number(rawAqiRadius);
  const safeAqiRadius = Number.isFinite(parsedAqiRadius)
    ? Math.min(180, Math.max(110, parsedAqiRadius))
    : 140;
  const gaugeRadius = `${safeAqiRadius}%`;
  const valueDecimal = extendedProps?.valuePrecision ?? 2;
  const titleText = extendedProps?.title || 'AQI';
  const detailFontSize = Number.isFinite(Number(extendedProps?.detailFontSize))
    ? Math.min(28, Math.max(14, Number(extendedProps?.detailFontSize)))
    : 22;

  return {
    series: [
      {
        type: 'gauge',
        detail: { show: false },
        data: [{ value: gaugeValue, name: titleText }],
        min: minValue,
        max: maxValue,
        itemStyle: {
          border: `5px solid ${theme === 'dark' ? '#2a2a2a' : '#FFFFFF'}`,
          color: '#FFFFFF',
        },
        axisLine: {
          lineStyle: {
            width: 32,
            color: [
              [0.16, '#00943D'],
              [0.32, '#47B273'],
              [0.48, '#FFD000'],
              [0.64, '#EE8C19'],
              [0.80, '#E56367'],
              [1, '#DC272D']
            ],
          },
        },
        axisLabel: {
          show: extendedProps?.showAxisLabel ?? false,
        },
        axisTick: {
          show: extendedProps?.showAxisTick ?? false,
        },
        splitLine: {
          show: extendedProps?.showSplitLine ?? false,
        },
        pointer: {
          show: extendedProps?.showPointer ?? true,
          length: '48%',
          width: 12,
          itemStyle: {
            color: '#DC272D',
            borderRadius: 8
          },
          offsetCenter: [0, '-85%'],
        },
        title: {
          show: extendedProps?.showTitle ?? true,
          offsetCenter: [0, '4%'],
          textStyle: {
            color: theme === 'dark' ? '#FFFFFF' : '#000000',
            fontSize: extendedProps?.titleFontSize || 28,
            fontWeight: extendedProps?.titleFontWeight || 700,
          },
          text: titleText,
        },
        detail: {
          offsetCenter: ['0%', '-14%'],
          color: theme === 'dark' ? '#FFFFFF' : '#000000',
          formatter: function (value) {
            return value.toFixed(valueDecimal);
          },
          textStyle: {
            fontSize: detailFontSize,
            fontWeight: 400,
          }
        },
        center: ['50%', '84%'],
        radius: gaugeRadius,
        startAngle: 180,
        endAngle: 0,
      }
    ]
  };
};

// Switch Gauge: Dual-value gauge with toggle switching capability
registry['Switch Gauge'] = (basicProps, extendedProps, datapoints) => {
  const theme = basicProps?.theme || 'light';
  const rawRadius = extendedProps?.radius;
  const parsedRadius =
    typeof rawRadius === 'string'
      ? Number.parseFloat(rawRadius.replace('%', ''))
      : Number(rawRadius);
  const safeRadius = Number.isFinite(parsedRadius)
    ? Math.min(180, Math.max(120, parsedRadius))
    : 132;
  const gaugeRadius = `${safeRadius}%`;
  const minValue = extendedProps?.minValue ?? 0;
  const maxValue = extendedProps?.maxValue ?? 10000;
  const valueDecimal = extendedProps?.valuePrecision ?? 2;
  const detailFontSize = Number.isFinite(Number(extendedProps?.detailFontSize))
    ? Math.min(22, Math.max(12, Number(extendedProps?.detailFontSize)))
    : 18;
  const titleFontSize = Number.isFinite(Number(extendedProps?.titleFontSize))
    ? Math.min(20, Math.max(12, Number(extendedProps?.titleFontSize)))
    : 16;
  
  // Primary and secondary values for switch mode
  const primaryValue = datapoints.length > 0 ? Number(datapoints[0]?.value ?? datapoints[0]?.y ?? 5000) : 5000;
  const secondaryValue = datapoints.length > 1 ? Number(datapoints[1]?.value ?? datapoints[1]?.y ?? 3000) : 3000;
  const displayValue = extendedProps?.switchMode === 'secondary' ? secondaryValue : primaryValue;
  const displayLabel = extendedProps?.switchMode === 'secondary' ? (extendedProps?.secondaryLabel || 'Secondary') : (extendedProps?.primaryLabel || 'Primary');

  return {
    series: [
      {
        type: 'gauge',
        detail: { show: false },
        data: [{ value: displayValue, name: displayLabel }],
        min: minValue,
        max: maxValue,
        itemStyle: {
          border: `4px solid ${theme === 'dark' ? '#2a2a2a' : '#FFFFFF'}`,
          color: '#FFFFFF',
        },
        axisLine: {
          lineStyle: {
            width: 30,
            color: [
              [0.25, '#00943D'],
              [0.5, '#FFD000'],
              [0.75, '#EE8C19'],
              [1, '#DC272D']
            ],
          },
        },
        axisLabel: {
          show: extendedProps?.showAxisLabel ?? true,
          distance: 8,
          color: basicProps?.textColor || '#56636E',
        },
        axisTick: {
          show: extendedProps?.showAxisTick ?? true,
          distance: -8,
          length: 5,
          lineStyle: {
            color: basicProps?.textColor || '#56636E'
          }
        },
        splitLine: {
          show: extendedProps?.showSplitLine ?? true,
          distance: 0,
          length: 8,
        },
        pointer: {
          show: extendedProps?.showPointer ?? true,
          length: '62%',
          width: 11,
          itemStyle: {
            color: extendedProps?.pointerColor || '#DC272D',
            borderRadius: 4
          },
          offsetCenter: [0, '-72%'],
        },
        title: {
          show: extendedProps?.showTitle ?? true,
          offsetCenter: [0, '18%'],
          textStyle: {
            color: theme === 'dark' ? '#FFFFFF' : '#000000',
            fontSize: titleFontSize,
            fontWeight: extendedProps?.titleFontWeight || 600,
          },
          text: displayLabel,
        },
        detail: {
          offsetCenter: ['0%', '-10%'],
          color: theme === 'dark' ? '#FFFFFF' : '#000000',
          formatter: function (value) {
            return value.toFixed(valueDecimal);
          },
          textStyle: {
            fontSize: detailFontSize,
            fontWeight: 500,
          }
        },
        center: ['50%', '86%'],
        radius: gaugeRadius,
        startAngle: 180,
        endAngle: 0,
      }
    ]
  };
};

// Simple Gauge: Radial bar gauge with consumption/target tracking
registry['Simple Gauge'] = (basicProps, extendedProps, datapoints) => {
  const theme = basicProps?.theme || 'light';
  const minValue = extendedProps?.minValue ?? 0;
  const maxValue = extendedProps?.maxValue ?? 8000;
  const valueDecimal = extendedProps?.valuePrecision ?? 2;
  
  // Extract values from datapoints
  const actualValue = datapoints.length > 0 ? Number(datapoints[0]?.value ?? datapoints[0]?.y ?? 5000) : 5000;
  const targetValue = datapoints.length > 1 ? Number(datapoints[1]?.value ?? datapoints[1]?.y ?? 6000) : 6000;
  const percentageConsumed = (actualValue / maxValue) * 100;
  
  const barColor = extendedProps?.barColor || '#1a90ff';
  const trackColor = theme === 'dark' ? '#404040' : '#e7e7e7';
  const titleText = extendedProps?.title || 'Consumption';

  return {
    series: [
      {
        type: 'gauge',
        startAngle: 225,
        endAngle: -45,
        radius: '75%',
        min: minValue,
        max: maxValue,
        splitNumber: 10,
        axisLine: {
          lineStyle: {
            width: 20,
            color: [
              [1, trackColor]
            ]
          }
        },
        pointer: {
          itemStyle: {
            color: barColor
          },
          length: '60%',
          width: 8,
        },
        axisTick: {
          show: extendedProps?.showAxisTick ?? true,
          distance: -8,
          length: 3,
          lineStyle: {
            color: basicProps?.textColor || '#56636E',
            width: 1
          }
        },
        axisLabel: {
          show: extendedProps?.showAxisLabel ?? true,
          distance: 8,
          fontSize: 10,
          color: basicProps?.textColor || '#56636E',
          formatter: function(value) {
            return value.toFixed(0);
          }
        },
        detail: {
          valueAnimation: true,
          formatter: '{value}%',
          offsetCenter: [0, '-30%'],
          textStyle: {
            fontSize: extendedProps?.detailFontSize || 24,
            fontWeight: 600,
            color: barColor
          }
        },
        data: [{ value: percentageConsumed, name: titleText }]
      }
    ]
  };
};

// Device Donut: Dual-ring donut chart showing device status (Connected/Disconnected)
registry['Device Donut'] = (basicProps, extendedProps, datapoints) => {
  const theme = basicProps?.theme || 'light';
  const titleText = extendedProps?.title || 'Devices';
  const titleFontSize = extendedProps?.titleFontSize || 14;
  const titleFontWeight = extendedProps?.titleFontWeight || 600;
  const titleFontFamily = extendedProps?.titleFontFamily || 'Roboto';
  
  // Demo data: Connected and Disconnected devices
  let connectedCount = 25;
  let disconnectedCount = 8;
  
  // Use datapoints if available
  if (Array.isArray(datapoints) && datapoints.length > 0) {
    connectedCount = Number(datapoints[0]?.value ?? datapoints[0]?.y ?? 25);
    disconnectedCount = datapoints.length > 1 ? Number(datapoints[1]?.value ?? datapoints[1]?.y ?? 8) : 8;
  }
  const connectedPoint = Array.isArray(datapoints)
    ? (datapoints.find((dp) => String(dp?.datapointName || dp?.name || '').toLowerCase().includes('connected') && !String(dp?.datapointName || dp?.name || '').toLowerCase().includes('disconnected')) || datapoints[0])
    : null;
  const disconnectedPoint = Array.isArray(datapoints)
    ? (datapoints.find((dp) => String(dp?.datapointName || dp?.name || '').toLowerCase().includes('disconnected')) || datapoints[1])
    : null;
  
  // Colors for outer ring and inner ring
  const outerColor = connectedPoint?.itemStyle?.color || connectedPoint?.color || extendedProps?.outerColor || '#47B273'; // Connected - Green
  const innerColor = disconnectedPoint?.itemStyle?.color || disconnectedPoint?.color || extendedProps?.innerColor || '#DC272D'; // Disconnected - Red
  const outerInnerColorLight = extendedProps?.outerInnerLight || '#EBFFF1';
  const outerInnerColorDark = extendedProps?.outerInnerDark || '#1a3a1a';
  const innerInnerColorLight = extendedProps?.innerInnerLight || '#FDECEC';
  const innerInnerColorDark = extendedProps?.innerInnerDark || '#3a1a1a';
  
  const borderColor = theme === 'dark' ? '#000000' : '#FFFFFF';
  const labelColor = theme === 'dark' ? '#FFFFFF' : '#000000';
  const textColor = basicProps?.textColor || (theme === 'dark' ? '#FFFFFF' : '#000000');
  const outerRadius = Math.min(95, Math.max(1, Number(extendedProps?.outerRadius ?? 80)));
  const innerRadius = Math.min(90, Math.max(0, Number(extendedProps?.innerRadius ?? 65)));
  const safeOuterRadius = Math.max(innerRadius + 1, outerRadius);
  const labelVisible = extendedProps?.label?.show ?? true;

  return {
    title: {
      text: titleText,
      textStyle: {
        fontSize: titleFontSize,
        fontWeight: titleFontWeight,
        fontFamily: titleFontFamily,
        color: textColor,
      },
      left: 'center',
      top: 'top',
    },
    tooltip: {
      trigger: 'item',
      backgroundColor: '#005C8E',
      borderColor: '#000000',
      borderRadius: [0, 0, 0, 8],
      borderWidth: 0,
      extraCssText: 'border-radius: 0 0 0 8px',
      textStyle: {
        color: '#FFFFFF',
        fontSize: 12,
        fontFamily: 'Roboto',
      },
      formatter: function (params) {
        return `<div style="display:flex; flex-direction:row"><div style="border-radius:10px; margin-top:4px; margin-right:5px; height:10px; width:10px; background-color:${params.color}"></div>${params.name}: ${params.value}</div>`;
      },
    },
    legend: {
      orient: 'horizontal',
      left: 'center',
      bottom: 0,
      textStyle: {
        color: textColor,
        fontSize: extendedProps?.legendFontSize || 12,
      },
    },
    series: [
      {
        name: 'Device Status',
        type: 'pie',
        radius: [`${innerRadius}%`, `${safeOuterRadius}%`],
        center: ['50%', '50%'],
        itemStyle: {
          borderColor: borderColor,
          borderWidth: extendedProps?.borderWidth ?? 2,
        },
        emphasis: {
          scaleSize: 0,
        },
        label: {
          position: 'inside',
          formatter: '{d}%',
          color: labelColor,
          fontSize: extendedProps?.labelFontSize || 11,
          fontWeight: extendedProps?.labelFontWeight || 700,
          fontFamily: 'Roboto',
        },
        data: [
          {
            value: connectedCount,
            name: 'Connected',
            itemStyle: {
              color: outerColor,
            },
            label: { show: labelVisible },
          },
          {
            value: disconnectedCount,
            name: 'Disconnected',
            itemStyle: {
              color: innerColor,
            },
            label: { show: labelVisible },
          },
        ],
      },
      {
        name: 'Inner Ring',
        type: 'pie',
        radius: [0, `${innerRadius}%`],
        center: ['50%', '50%'],
        itemStyle: {
          borderColor: borderColor,
          borderWidth: extendedProps?.innerBorderWidth ?? 2,
        },
        emphasis: {
          scaleSize: 0,
        },
        label: {
          position: 'inside',
          formatter: '{c}',
          color: labelColor,
          fontSize: extendedProps?.innerLabelFontSize || 12,
          fontWeight: extendedProps?.innerLabelFontWeight || 700,
        },
        data: [
          {
            value: connectedCount,
            name: 'Connected',
            itemStyle: {
              color: theme === 'light' ? outerInnerColorLight : outerInnerColorDark,
            },
            label: {
              show: labelVisible,
              color: labelColor,
            },
          },
          {
            value: disconnectedCount,
            name: 'Disconnected',
            itemStyle: {
              color: theme === 'light' ? innerInnerColorLight : innerInnerColorDark,
            },
            label: {
              show: labelVisible,
              color: labelColor,
            },
          },
        ],
      },
    ],
  };
};

// DeviceTable: Specialized table widget displaying device information with connection status
registry['DeviceTable'] = (basicProps, extendedProps, datapoints) => {
  const theme = basicProps?.theme || 'light';
  const titleText = extendedProps?.title || 'Devices';
  
  // Return empty ECharts options since DeviceTable is rendered as HTML table, not as chart
  // The actual table rendering is handled in the Dashboard/Widget rendering components
  return {
    title: { text: titleText, left: 'center', textStyle: { color: theme === 'dark' ? '#E8E8E8' : '#333' } },
    xAxis: { type: 'category', data: [], show: false },
    yAxis: { show: false },
    series: [],
    grid: { show: false }
  };
};

// TableGateway: Specialized table widget displaying gateway information with connection status
registry['TableGateway'] = (basicProps, extendedProps, datapoints) => {
  const theme = basicProps?.theme || 'light';
  const titleText = extendedProps?.title || 'Gateways';
  
  // Return empty ECharts options since TableGateway is rendered as HTML table, not as chart
  // The actual table rendering is handled in the Dashboard/Widget rendering components
  return {
    title: { text: titleText, left: 'center', textStyle: { color: theme === 'dark' ? '#E8E8E8' : '#333' } },
    xAxis: { type: 'category', data: [], show: false },
    yAxis: { show: false },
    series: [],
    grid: { show: false }
  };
};

// Gateway Donut: Dual-ring donut chart showing gateway status (Connected/Disconnected)
registry['Gateway Donut'] = (basicProps, extendedProps, datapoints) => {
  const theme = basicProps?.theme || 'light';
  const titleText = extendedProps?.title || 'Gateways';
  const titleFontSize = extendedProps?.titleFontSize || 14;
  const titleFontWeight = extendedProps?.titleFontWeight || 600;
  const titleFontFamily = extendedProps?.titleFontFamily || 'Roboto';
  
  // Demo data: Connected and Disconnected gateways
  let connectedCount = 12;
  let disconnectedCount = 3;
  
  // Use datapoints if available
  if (Array.isArray(datapoints) && datapoints.length > 0) {
    connectedCount = Number(datapoints[0]?.value ?? datapoints[0]?.y ?? 12);
    disconnectedCount = datapoints.length > 1 ? Number(datapoints[1]?.value ?? datapoints[1]?.y ?? 3) : 3;
  }
  const connectedPoint = Array.isArray(datapoints)
    ? (datapoints.find((dp) => String(dp?.datapointName || dp?.name || '').toLowerCase().includes('connected') && !String(dp?.datapointName || dp?.name || '').toLowerCase().includes('disconnected')) || datapoints[0])
    : null;
  const disconnectedPoint = Array.isArray(datapoints)
    ? (datapoints.find((dp) => String(dp?.datapointName || dp?.name || '').toLowerCase().includes('disconnected')) || datapoints[1])
    : null;
  
  // Colors for outer ring and inner ring
  const outerColor = connectedPoint?.itemStyle?.color || connectedPoint?.color || extendedProps?.outerColor || '#47B273'; // Connected - Green
  const innerColor = disconnectedPoint?.itemStyle?.color || disconnectedPoint?.color || extendedProps?.innerColor || '#DC272D'; // Disconnected - Red
  const outerInnerColorLight = extendedProps?.outerInnerLight || '#EBFFF1';
  const outerInnerColorDark = extendedProps?.outerInnerDark || '#1a3a1a';
  const innerInnerColorLight = extendedProps?.innerInnerLight || '#FDECEC';
  const innerInnerColorDark = extendedProps?.innerInnerDark || '#3a1a1a';
  
  const borderColor = theme === 'dark' ? '#000000' : '#FFFFFF';
  const labelColor = theme === 'dark' ? '#FFFFFF' : '#000000';
  const textColor = basicProps?.textColor || (theme === 'dark' ? '#FFFFFF' : '#000000');
  const outerRadius = Math.min(95, Math.max(1, Number(extendedProps?.outerRadius ?? 80)));
  const innerRadius = Math.min(90, Math.max(0, Number(extendedProps?.innerRadius ?? 65)));
  const safeOuterRadius = Math.max(innerRadius + 1, outerRadius);
  const labelVisible = extendedProps?.label?.show ?? true;

  return {
    title: {
      text: titleText,
      textStyle: {
        fontSize: titleFontSize,
        fontWeight: titleFontWeight,
        fontFamily: titleFontFamily,
        color: textColor,
      },
      left: 'center',
      top: 'top',
    },
    tooltip: {
      trigger: 'item',
      backgroundColor: '#005C8E',
      borderColor: '#000000',
      borderRadius: [0, 0, 0, 8],
      borderWidth: 0,
      extraCssText: 'border-radius: 0 0 0 8px',
      textStyle: {
        color: '#FFFFFF',
        fontSize: 12,
        fontFamily: 'Roboto',
      },
      formatter: function (params) {
        return `<div style="display:flex; flex-direction:row"><div style="border-radius:10px; margin-top:4px; margin-right:5px; height:10px; width:10px; background-color:${params.color}"></div>${params.name}: ${params.value}</div>`;
      },
    },
    legend: {
      orient: 'horizontal',
      left: 'center',
      bottom: 0,
      textStyle: {
        color: textColor,
        fontSize: extendedProps?.legendFontSize || 12,
      },
    },
    series: [
      {
        name: 'Gateway Status',
        type: 'pie',
        radius: [`${innerRadius}%`, `${safeOuterRadius}%`],
        center: ['50%', '50%'],
        itemStyle: {
          borderColor: borderColor,
          borderWidth: extendedProps?.borderWidth ?? 2,
        },
        emphasis: {
          scaleSize: 0,
        },
        label: {
          position: 'inside',
          formatter: '{d}%',
          color: labelColor,
          fontSize: extendedProps?.labelFontSize || 11,
          fontWeight: extendedProps?.labelFontWeight || 700,
          fontFamily: 'Roboto',
        },
        data: [
          {
            value: connectedCount,
            name: 'Connected',
            itemStyle: {
              color: outerColor,
            },
            label: { show: labelVisible },
          },
          {
            value: disconnectedCount,
            name: 'Disconnected',
            itemStyle: {
              color: innerColor,
            },
            label: { show: labelVisible },
          },
        ],
      },
      {
        name: 'Inner Ring',
        type: 'pie',
        radius: [0, `${innerRadius}%`],
        center: ['50%', '50%'],
        itemStyle: {
          borderColor: borderColor,
          borderWidth: extendedProps?.innerBorderWidth ?? 2,
        },
        emphasis: {
          scaleSize: 0,
        },
        label: {
          position: 'inside',
          formatter: '{c}',
          color: labelColor,
          fontSize: extendedProps?.innerLabelFontSize || 12,
          fontWeight: extendedProps?.innerLabelFontWeight || 700,
        },
        data: [
          {
            value: connectedCount,
            name: 'Connected',
            itemStyle: {
              color: theme === 'light' ? outerInnerColorLight : outerInnerColorDark,
            },
            label: {
              show: labelVisible,
              color: labelColor,
            },
          },
          {
            value: disconnectedCount,
            name: 'Disconnected',
            itemStyle: {
              color: theme === 'light' ? innerInnerColorLight : innerInnerColorDark,
            },
            label: {
              show: labelVisible,
              color: labelColor,
            },
          },
        ],
      },
    ],
  };
};

export function getChartOptions(type, basicProps, extendedProps = {}, datapoints = []) {
  // Support charts, gauges, and tables
  const builder = registry[type];
  if (!builder) {
    console.warn(`[chartOptionsFactory] No builder found for type: "${type}". Available types:`, Object.keys(registry));
    return { series: [] };
  }
  try {
    const opts = builder(basicProps, extendedProps, Array.isArray(datapoints) ? datapoints : []);
    // Ensure every series has a data array to avoid getRawIndex on undefined
    if (Array.isArray(opts.series)) {
      opts.series = opts.series.map(s => ({ ...s, data: Array.isArray(s.data) ? s.data : [] }));
    }
    return opts;
  } catch (e) {
    console.warn('[chartOptionsFactory] builder error for type:', type, e);
    return { series: [] };
  }
}

// Allow external registration for future chart types
export function registerChartType(type, builderFn) {
  registry[type] = builderFn;
}

export function listRegisteredChartTypes() { return Object.keys(registry); }